import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { clearAuth } from '../services/authService';
import DemoOverlay from '../demo/DemoOverlay';
import AiChatPanel from './AiChatPanel';
import { getWorkTabLabel } from '../utils/routeLabel';
import { endDemoSession } from '../demo/demoMode';
import { useColorMode, toggleColorMode } from '../lib/colorMode';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import { API_URL } from '../config/apiConfig';
import { getProductList, getProductDetails } from '../services/productService';
import { getReservationTravelerList, getConsultList } from '../services/apiService';
import {
  HomeIcon as HomeIconSolid,
  MapPinIcon as MapPinIconSolid,
  CalendarIcon as CalendarIconSolid,
  UserCircleIcon,
  DocumentTextIcon as DocumentTextIconSolid,
  ChevronDownIcon,
  Cog6ToothIcon as Cog6ToothIconSolid,
  PresentationChartBarIcon as PerformanceIconSolid,
  ChartPieIcon as ChartPieIconSolid,
  BanknotesIcon as BanknotesIconSolid,
} from '@heroicons/react/24/solid';
import {
  HomeIcon as HomeIconOutline,
  MapPinIcon as MapPinIconOutline,
  CalendarIcon as CalendarIconOutline,
  DocumentTextIcon as DocumentTextIconOutline,
  Cog6ToothIcon as Cog6ToothIconOutline,
  PresentationChartBarIcon as PerformanceIconOutline,
  PencilSquareIcon,
  KeyIcon,
  BuildingOfficeIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  MapIcon,
  CubeIcon,
  BanknotesIcon as BanknotesIconOutline,
  CreditCardIcon,
  Squares2X2Icon,
  TagIcon,
  TicketIcon,
  PaperAirplaneIcon,
  ChartPieIcon as ChartPieIconOutline,
  LightBulbIcon,
  SunIcon,
  MoonIcon,
  XMarkIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';

import SmartGuideModal from './SmartGuideModal';
import ExchangeRateWidget from './ExchangeRateWidget';

const DEFAULT_PROFILE_IMAGE = 'https://kr.object.iwinv.kr/orgin/1/Product/112524/Editor/png-transparent-profle-person-profile-user-circle-icons-icon-thumbnail.png';
const WORK_TABS_STORAGE_KEY = 'trp_work_tabs_v1';
const WORK_TAB_DRAFTS_STORAGE_KEY = 'trp_work_tab_drafts_v1';
const MAX_WORK_TABS = 12;
/** 같은 메뉴(그룹) 탭 안에 유지할 최대 상품/화면 수 */
const MAX_ENTRIES_PER_WORK_TAB_GROUP = 24;

/** 봄 시즌 헤더 벚꽃 애니메이션 — 내년 봄에 true로 바꾸면 다시 표시 */
const ENABLE_HEADER_CHERRY_BLOSSOM = false;

const getFieldIdentity = (el, index) => {
  const tag = (el.tagName || '').toLowerCase();
  const type = (el.type || '').toLowerCase();
  const name = el.getAttribute('name') || '';
  const id = el.getAttribute('id') || '';
  const dataField = el.getAttribute('data-field') || '';
  const placeholder = el.getAttribute('placeholder') || '';

  return [
    `tag:${tag}`,
    `type:${type}`,
    `name:${name}`,
    `id:${id}`,
    `data:${dataField}`,
    `placeholder:${placeholder}`,
    `idx:${index}`,
  ].join('|');
};

const readDraftStore = () => {
  try {
    const raw = window.sessionStorage.getItem(WORK_TAB_DRAFTS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch (e) {
    return {};
  }
};

const writeDraftStore = (next) => {
  try {
    window.sessionStorage.setItem(WORK_TAB_DRAFTS_STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    // ignore storage failures
  }
};

const collectRouteDraft = () => {
  const contentRoot = document.querySelector('main');
  if (!contentRoot) return { fields: [], scrollTop: 0 };

  const nodes = Array.from(contentRoot.querySelectorAll('input, textarea, select'));
  const fields = nodes.reduce((acc, el, index) => {
    const tag = (el.tagName || '').toLowerCase();
    const type = (el.type || '').toLowerCase();
    if (type === 'password' || type === 'file') return acc;

    const identity = getFieldIdentity(el, index);

    if (tag === 'select' && el.multiple) {
      const selectedValues = Array.from(el.options || [])
        .filter((opt) => opt.selected)
        .map((opt) => opt.value);
      acc.push({ identity, tag, type, selectedValues });
      return acc;
    }

    if (type === 'checkbox' || type === 'radio') {
      acc.push({ identity, tag, type, checked: !!el.checked });
      return acc;
    }

    acc.push({ identity, tag, type, value: el.value ?? '' });
    return acc;
  }, []);

  return {
    fields,
    scrollTop: contentRoot.scrollTop || 0,
  };
};

const applyRouteDraft = (draft) => {
  if (!draft || !Array.isArray(draft.fields)) return;

  const contentRoot = document.querySelector('main');
  if (!contentRoot) return;

  const nodes = Array.from(contentRoot.querySelectorAll('input, textarea, select'));
  if (nodes.length === 0 && draft.fields.length > 0) return;

  const nodeMap = new Map(nodes.map((el, index) => [getFieldIdentity(el, index), el]));

  draft.fields.forEach((field) => {
    const el = nodeMap.get(field.identity);
    if (!el || el.disabled) return;

    const type = (el.type || '').toLowerCase();
    const tag = (el.tagName || '').toLowerCase();

    if (tag === 'select' && el.multiple && Array.isArray(field.selectedValues)) {
      const selectedSet = new Set(field.selectedValues);
      Array.from(el.options || []).forEach((opt) => {
        opt.selected = selectedSet.has(opt.value);
      });
      el.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (type === 'checkbox' || type === 'radio') {
      if (typeof field.checked === 'boolean') {
        el.checked = field.checked;
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    if (typeof field.value === 'string') {
      el.value = field.value;
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  if (typeof draft.scrollTop === 'number') {
    contentRoot.scrollTop = draft.scrollTop;
  }
};


const QUOTE_DETAIL_PATH_PREFIX = '/quote-detail/';

const firstPathSegmentAfterPrefix = (pathname, prefix) => {
  if (!pathname || !pathname.startsWith(prefix)) return null;
  const first = pathname.slice(prefix.length).split('/')[0];
  if (!first) return null;
  try {
    return decodeURIComponent(first);
  } catch {
    return first;
  }
};

const getQuoteDetailPathSegment = (pathname) =>
  firstPathSegmentAfterPrefix(pathname, QUOTE_DETAIL_PATH_PREFIX);

const pickProductDisplayNameFromLocationState = (state) => {
  if (!state || typeof state !== 'object') return '';
  const raw = state.productName ?? state.product?.상품명 ?? state.상품명;
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s;
};

const PATH_PREFIXES_FOR_PRODUCT_NO_FETCH = [
  QUOTE_DETAIL_PATH_PREFIX,
  '/price-stock/',
  '/detail-settings/',
  '/general/price-stock/',
  '/general/detail-settings/',
];

/** URL에서 숫자 상품번호만 추출 (API 조회용). 문자 코스/주문코드는 null */
const extractNumericProductNoFromPath = (pathname) => {
  if (!pathname) return null;
  for (const prefix of PATH_PREFIXES_FOR_PRODUCT_NO_FETCH) {
    if (!pathname.startsWith(prefix)) continue;
    const seg = firstPathSegmentAfterPrefix(pathname, prefix);
    if (seg && /^\d+$/.test(seg) && Number(seg) > 0) return Number(seg);
  }
  if (pathname.startsWith('/orders/')) {
    const seg = firstPathSegmentAfterPrefix(pathname, '/orders/');
    if (seg && seg !== '-' && /^\d+$/.test(seg) && Number(seg) > 0) return Number(seg);
  }
  if (pathname.startsWith('/general/orders/')) {
    const seg = firstPathSegmentAfterPrefix(pathname, '/general/orders/');
    if (seg && seg !== '-' && /^\d+$/.test(seg) && Number(seg) > 0) return Number(seg);
  }
  return null;
};

/** 작업 탭 호버 시 상품/주문 식별 정보 (브라우저 기본 툴팁) */
const getWorkTabHoverTitle = (tab, activeWorkTabIdForHover) => {
  if (tab.kind === 'group') {
    const n = tab.entries?.length || 0;
    const cur = tab.entries?.find((e) => e.id === activeWorkTabIdForHover);
    const curName = cur?.productDisplayName?.trim();
    const base = `${tab.label} · ${n}개`;
    if (curName) return `${base} — 현재: ${curName}`;
    return base;
  }

  const pathname = tab.path;
  const label = tab.label;
  const productDisplayName = tab.productDisplayName;

  const base = label || getWorkTabLabel(pathname);
  if (productDisplayName && String(productDisplayName).trim()) {
    return `${base} — ${String(productDisplayName).trim()}`;
  }
  if (!pathname) return base;

  let detail = '';
  if (pathname.startsWith(QUOTE_DETAIL_PATH_PREFIX)) {
    const seg = getQuoteDetailPathSegment(pathname);
    if (seg === '-') detail = '상품 연결 전';
    else if (seg && /^\d+$/.test(seg) && Number(seg) > 0) detail = `상품번호 ${seg}`;
    else if (seg) detail = `상품코드 ${seg}`;
  } else if (pathname.startsWith('/price-stock/')) {
    const seg = firstPathSegmentAfterPrefix(pathname, '/price-stock/');
    if (seg) detail = `상품번호 ${seg}`;
  } else if (pathname.startsWith('/detail-settings/')) {
    const seg = firstPathSegmentAfterPrefix(pathname, '/detail-settings/');
    if (seg) detail = `상품번호 ${seg}`;
  } else if (pathname.startsWith('/orders/')) {
    const seg = firstPathSegmentAfterPrefix(pathname, '/orders/');
    if (seg) detail = `상품코드 ${seg}`;
  } else if (pathname.startsWith('/general/price-stock/')) {
    const seg = firstPathSegmentAfterPrefix(pathname, '/general/price-stock/');
    if (seg) detail = `상품번호 ${seg}`;
  } else if (pathname.startsWith('/general/detail-settings/')) {
    const seg = firstPathSegmentAfterPrefix(pathname, '/general/detail-settings/');
    if (seg) detail = `상품번호 ${seg}`;
  } else if (pathname.startsWith('/general/orders/')) {
    const seg = firstPathSegmentAfterPrefix(pathname, '/general/orders/');
    if (seg) detail = `상품코드 ${seg}`;
  } else if (pathname.startsWith('/reservation-detail/')) {
    const seg = firstPathSegmentAfterPrefix(pathname, '/reservation-detail/');
    if (seg) detail = `주문번호 ${seg}`;
  } else if (pathname.startsWith('/other-company-reservation-detail/')) {
    const seg = firstPathSegmentAfterPrefix(pathname, '/other-company-reservation-detail/');
    if (seg) detail = `주문번호 ${seg}`;
  }

  if (!detail) return base;
  return `${base} — ${detail}`;
};

const getWorkTabEntryDropdownLabel = (entry) => {
  if (entry.productDisplayName?.trim()) return entry.productDisplayName.trim();
  const no = extractNumericProductNoFromPath(entry.path);
  if (no != null) return `상품번호 ${no}`;
  const tail = (entry.path || '').split('/').filter(Boolean).pop();
  return tail || entry.path || '';
};

/** 상담 상세 URL이 /quote-detail/{상품번호} 형태(숫자)로 정리된 뒤의 주소 */
const isCanonicalNumericQuoteDetailPath = (pathname) => {
  const seg = getQuoteDetailPathSegment(pathname);
  return seg != null && /^\d+$/.test(seg) && Number(seg) > 0;
};

/** 같은 메뉴(예: 상세페이지 설정)으로 묶을 경로 → 그룹 키·표시 라벨 */
const getWorkTabGroupMeta = (pathname) => {
  if (!pathname) return null;
  const rules = [
    ['/general/detail-settings/', 'general-detail-settings', '일반 상세페이지 설정'],
    ['/general/price-stock/', 'general-price-stock', '일반 가격/재고'],
    ['/general/orders/', 'general-orders', '일반 예약자 목록'],
    ['/detail-settings/', 'detail-settings', '상세페이지 설정'],
    ['/price-stock/', 'price-stock', '가격/재고'],
    ['/orders/', 'orders', '예약자 목록'],
    [QUOTE_DETAIL_PATH_PREFIX, 'quote-detail', '상담 상세'],
    ['/reservation-detail/', 'reservation-detail', '예약 상세'],
    ['/other-company-reservation-detail/', 'other-company-reservation-detail', '타사상품 판매 상세'],
  ];
  for (const [prefix, groupKey, menuLabel] of rules) {
    if (pathname.startsWith(prefix)) return { groupKey, menuLabel };
  }
  return null;
};

const pruneSupersededQuoteDetailEntries = (entries, nextPathname) => {
  if (!isCanonicalNumericQuoteDetailPath(nextPathname)) return entries;
  const targetSeg = getQuoteDetailPathSegment(nextPathname);
  return entries.filter((e) => {
    if (!e.path || !e.path.startsWith(QUOTE_DETAIL_PATH_PREFIX)) return true;
    const seg = getQuoteDetailPathSegment(e.path);
    if (seg == null) return true;
    if (seg === targetSeg) return true;
    if (seg === '-') return false;
    if (!/^\d+$/.test(seg)) return false;
    return true;
  });
};

/**
 * 상담 목록 → /quote-detail/- → replace로 /quote-detail/123 이 되는 동안
 * placeholder·비숫자 URL 항목을 그룹/단순 탭에서 제거합니다.
 */
const pruneQuoteDetailInTabList = (tabs, nextPathname) => {
  if (!isCanonicalNumericQuoteDetailPath(nextPathname)) return tabs;
  const targetSeg = getQuoteDetailPathSegment(nextPathname);
  return tabs
    .map((tab) => {
      if (tab.kind === 'group' && tab.groupKey === 'quote-detail') {
        const entries = pruneSupersededQuoteDetailEntries(tab.entries || [], nextPathname);
        return !entries.length ? null : { ...tab, entries };
      }
      if (tab.kind === 'group') return tab;
      if (!tab.path || !tab.path.startsWith(QUOTE_DETAIL_PATH_PREFIX)) return tab;
      const seg = getQuoteDetailPathSegment(tab.path);
      if (seg == null) return tab;
      if (seg === targetSeg) return tab;
      if (seg === '-') return null;
      if (!/^\d+$/.test(seg)) return null;
      return tab;
    })
    .filter(Boolean);
};

const buildWorkTabEntryPayload = (parts) => ({
  id: parts.id,
  path: parts.path,
  search: parts.search || '',
  hash: parts.hash || '',
  ...(parts.productDisplayName ? { productDisplayName: String(parts.productDisplayName).trim() } : {}),
});

/** 세션에 저장된 플랫 탭 목록 → 메뉴별 그룹 탭으로 합침 */
const migrateFlatTabsToGrouped = (flat) => {
  const result = [];
  for (const tab of flat) {
    if (!tab || tab.path === '/') continue;
    const meta = getWorkTabGroupMeta(tab.path);
    if (!meta) {
      result.push({
        kind: 'simple',
        id: tab.id,
        path: tab.path,
        search: tab.search || '',
        hash: tab.hash || '',
        label: getWorkTabLabel(tab.path),
        ...(tab.productDisplayName ? { productDisplayName: tab.productDisplayName } : {}),
      });
      continue;
    }
    const entry = buildWorkTabEntryPayload({
      id: tab.id,
      path: tab.path,
      search: tab.search,
      hash: tab.hash,
      productDisplayName: tab.productDisplayName,
    });
    const gi = result.findIndex((t) => t.kind === 'group' && t.groupKey === meta.groupKey);
    if (gi >= 0) {
      const g = result[gi];
      if (!g.entries.some((e) => e.id === entry.id)) {
        const nextEntries = [...g.entries, entry].slice(-MAX_ENTRIES_PER_WORK_TAB_GROUP);
        result[gi] = { ...g, entries: nextEntries };
      }
    } else {
      result.push({
        kind: 'group',
        groupKey: meta.groupKey,
        label: meta.menuLabel,
        entries: [entry],
      });
    }
  }
  return result.slice(-MAX_WORK_TABS);
};

const normalizeWorkTabsFromStorage = (parsed) => {
  if (!Array.isArray(parsed)) return [];
  const hasGrouped = parsed.some((t) => t && t.kind === 'group');
  if (hasGrouped) {
    return parsed
      .filter((tab) => {
        if (!tab) return false;
        if (tab.kind === 'group') {
          return tab.groupKey && Array.isArray(tab.entries) && tab.entries.length > 0;
        }
        return typeof tab.id === 'string' && typeof tab.path === 'string' && tab.path !== '/';
      })
      .slice(-MAX_WORK_TABS);
  }
  return migrateFlatTabsToGrouped(parsed);
};

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const colorMode = useColorMode();
  const [userProfile, setUserProfile] = useState({ companyName: '', userName: '', companyLogo: '' });
  const [userInfo, setUserInfo] = useState(null);
  const [originalStaffInfo, setOriginalStaffInfo] = useState(null);
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordChangeMessage, setPasswordChangeMessage] = useState('');
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isLoadingCompanyInfo, setIsLoadingCompanyInfo] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [originalCompanyInfo, setOriginalCompanyInfo] = useState(null);
  const [companyModalTab, setCompanyModalTab] = useState('company');
  const [companyStaffList, setCompanyStaffList] = useState(null);
  const [isLoadingCompanyStaffList, setIsLoadingCompanyStaffList] = useState(false);
  const [companyStaffListError, setCompanyStaffListError] = useState('');
  const [showResignedStaff, setShowResignedStaff] = useState(false);
  const [selectedCompanyStaffRow, setSelectedCompanyStaffRow] = useState(null);
  const [selectedCompanyStaffDetail, setSelectedCompanyStaffDetail] = useState(null);
  const [isLoadingCompanyStaffDetail, setIsLoadingCompanyStaffDetail] = useState(false);
  const [isSavingCompanyStaff, setIsSavingCompanyStaff] = useState(false);
  const [companyStaffSaveError, setCompanyStaffSaveError] = useState('');
  const [companyStaffEditForm, setCompanyStaffEditForm] = useState({ isAdmin: false, isSettlementAdmin: false, isEmployed: true, nameKr: '' });
  const [companyStaffEditorMode, setCompanyStaffEditorMode] = useState(null);
  const [companyStaffCreateForm, setCompanyStaffCreateForm] = useState({ loginId: '', nameKr: '', phone: '', isAdmin: false, isSettlementAdmin: false });
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState('');
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [profileUploadError, setProfileUploadError] = useState('');
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [companySaveError, setCompanySaveError] = useState('');
  const [isSavingUserInfo, setIsSavingUserInfo] = useState(false);
  const [userInfoSaveError, setUserInfoSaveError] = useState('');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [rssItems, setRssItems] = useState([]);
  const [currentRssIndex, setCurrentRssIndex] = useState(0);

  // 통합 검색 상태 (상품/예약/상담)
  const [globalSearchDomain, setGlobalSearchDomain] = useState('product'); // 'product' | 'reservation' | 'consult'
  const [globalSearchKeyword, setGlobalSearchKeyword] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isGlobalSearchLoading, setIsGlobalSearchLoading] = useState(false);
  const [globalSearchError, setGlobalSearchError] = useState('');
  const [isSmartGuideOpen, setIsSmartGuideOpen] = useState(false);
  const [smartGuideTab, setSmartGuideTab] = useState('price');
  const [smartGuideDontShow, setSmartGuideDontShow] = useState(false);
  const [smartGuideFromTip, setSmartGuideFromTip] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const activeWorkTabId = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  const [workTabs, setWorkTabs] = useState([]);
  const [openWorkTabGroupKey, setOpenWorkTabGroupKey] = useState(null);
  const [workTabDropdownPos, setWorkTabDropdownPos] = useState(null);
  const workTabGroupWrapRefs = useRef({});
  const workTabHoverCloseTimerRef = useRef(null);
  const draftRestoreTimersRef = useRef([]);
  const workTabProductFetchInFlightRef = useRef(new Set());

  const clearWorkTabHoverCloseTimer = () => {
    if (workTabHoverCloseTimerRef.current != null) {
      window.clearTimeout(workTabHoverCloseTimerRef.current);
      workTabHoverCloseTimerRef.current = null;
    }
  };

  const scheduleWorkTabHoverClose = () => {
    clearWorkTabHoverCloseTimer();
    workTabHoverCloseTimerRef.current = window.setTimeout(() => {
      setOpenWorkTabGroupKey(null);
      workTabHoverCloseTimerRef.current = null;
    }, 140);
  };

  // Determine if current route is within products area for default accordion open
  const isInProductsArea = (
    path.startsWith('/products') ||
    path.startsWith('/detail-settings') ||
    path.startsWith('/price-stock') ||
    path.startsWith('/quick-wizard') ||
    path.startsWith('/orders')
  );

  const TIP_SEQUENCE = ['price', 'product', 'email', 'schedule', 'consult', 'consult-flow'];
  const currentSmartGuideTipIndexRaw = TIP_SEQUENCE.indexOf(smartGuideTab);
  const currentSmartGuideTipIndex = currentSmartGuideTipIndexRaw === -1 ? 0 : currentSmartGuideTipIndexRaw;
  const hasPrevSmartGuideTip = TIP_SEQUENCE.length > 1;
  const hasNextSmartGuideTip = TIP_SEQUENCE.length > 1;

  const handlePrevSmartGuideTip = () => {
    if (!hasPrevSmartGuideTip) return;
    const nextIndex = (currentSmartGuideTipIndex - 1 + TIP_SEQUENCE.length) % TIP_SEQUENCE.length;
    setSmartGuideTab(TIP_SEQUENCE[nextIndex]);
  };

  const handleNextSmartGuideTip = () => {
    if (!hasNextSmartGuideTip) return;
    const nextIndex = (currentSmartGuideTipIndex + 1) % TIP_SEQUENCE.length;
    setSmartGuideTab(TIP_SEQUENCE[nextIndex]);
  };

  const [productsOpen, setProductsOpen] = useState(isInProductsArea);
  const [isSettingsLauncherOpen, setIsSettingsLauncherOpen] = useState(false);
  const [settingsLauncherKeyword, setSettingsLauncherKeyword] = useState('');

  // Flyout state for collapsed sidebar
  const [activeFlyout, setActiveFlyout] = useState(null); // 'products' | null
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0, left: 0 });
  const flyoutTimerRef = useRef(null);
  const profileMenuRef = useRef(null);
  const logoFileInputRef = useRef(null);
  const profileFileInputRef = useRef(null);
  const productsMenuRef = useRef(null);
  const globalSearchRef = useRef(null);

  const isAdminUser = !!(originalStaffInfo && originalStaffInfo.관리자여부);

  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(WORK_TABS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setWorkTabs(normalizeWorkTabsFromStorage(parsed));
    } catch (e) {
      // ignore broken storage payload
    }
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(WORK_TABS_STORAGE_KEY, JSON.stringify(workTabs));
    } catch (e) {
      // ignore storage failures (private mode/quota)
    }
  }, [workTabs]);

  useEffect(() => {
    if (!location.pathname || location.pathname === '/login') return;

    if (location.pathname === '/') {
      setWorkTabs((prev) => prev.filter((tab) => tab.path !== '/'));
      return;
    }

    const fromState = pickProductDisplayNameFromLocationState(location.state);
    const groupMeta = getWorkTabGroupMeta(location.pathname);

    const entryPayload = buildWorkTabEntryPayload({
      id: activeWorkTabId,
      path: location.pathname,
      search: location.search,
      hash: location.hash,
      productDisplayName: fromState || undefined,
    });

    const simpleTabPayload = {
      kind: 'simple',
      id: activeWorkTabId,
      path: location.pathname,
      search: location.search || '',
      hash: location.hash || '',
      label: getWorkTabLabel(location.pathname),
      ...(fromState ? { productDisplayName: fromState } : {}),
    };

    setWorkTabs((prev) => {
      const prevWithoutRoot = prev.filter((tab) => {
        if (tab.kind === 'group') return true;
        return tab.path !== '/';
      });
      const prunedList = pruneQuoteDetailInTabList(prevWithoutRoot, location.pathname);

      const stripEmptyGroups = (tabs) =>
        tabs.filter((t) => !(t.kind === 'group' && (!t.entries || t.entries.length === 0)));

      if (!groupMeta) {
        const existingIndex = prunedList.findIndex(
          (tab) => tab.kind !== 'group' && tab.id === simpleTabPayload.id
        );
        if (existingIndex >= 0) {
          const prevRow = prunedList[existingIndex];
          const resolvedName = fromState || prevRow.productDisplayName;
          const next = [...prunedList];
          next[existingIndex] = {
            ...prevRow,
            ...simpleTabPayload,
            ...(resolvedName ? { productDisplayName: String(resolvedName).trim() } : {}),
          };
          return stripEmptyGroups(next);
        }
        return stripEmptyGroups([...prunedList, simpleTabPayload].slice(-MAX_WORK_TABS));
      }

      const { groupKey, menuLabel } = groupMeta;
      const gi = prunedList.findIndex((t) => t.kind === 'group' && t.groupKey === groupKey);

      if (gi >= 0) {
        const g = prunedList[gi];
        let entries = pruneSupersededQuoteDetailEntries([...(g.entries || [])], location.pathname);
        const ei = entries.findIndex((e) => e.id === entryPayload.id);
        if (ei >= 0) {
          const resolvedName =
            (fromState && String(fromState).trim()) ||
            entries[ei].productDisplayName ||
            '';
          entries[ei] = {
            ...entries[ei],
            ...entryPayload,
            ...(resolvedName ? { productDisplayName: String(resolvedName).trim() } : {}),
          };
        } else {
          entries.push(entryPayload);
        }
        entries = pruneSupersededQuoteDetailEntries(entries, location.pathname).slice(
          -MAX_ENTRIES_PER_WORK_TAB_GROUP
        );
        const next = [...prunedList];
        next[gi] = { ...g, label: menuLabel, entries };
        return stripEmptyGroups(next);
      }

      const newGroup = {
        kind: 'group',
        groupKey,
        label: menuLabel,
        entries: pruneSupersededQuoteDetailEntries([entryPayload], location.pathname).slice(
          -MAX_ENTRIES_PER_WORK_TAB_GROUP
        ),
      };
      return stripEmptyGroups([...prunedList, newGroup].slice(-MAX_WORK_TABS));
    });
  }, [activeWorkTabId, location.hash, location.pathname, location.search, location.state]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { companyNo } = getUserInfo() || {};
      if (!companyNo) return;

      const toResolve = [];
      workTabs.forEach((tab) => {
        if (tab.kind === 'group') {
          (tab.entries || []).forEach((entry) => {
            if (!(entry.productDisplayName && String(entry.productDisplayName).trim())) {
              const no = extractNumericProductNoFromPath(entry.path);
              if (no != null) toResolve.push({ kind: 'entry', entryId: entry.id, path: entry.path });
            }
          });
        } else if (!(tab.productDisplayName && String(tab.productDisplayName).trim())) {
          const no = extractNumericProductNoFromPath(tab.path);
          if (no != null) toResolve.push({ kind: 'simple', entryId: tab.id, path: tab.path });
        }
      });
      if (toResolve.length === 0) return;

      const byProductNo = new Map();
      toResolve.forEach((item) => {
        const no = extractNumericProductNoFromPath(item.path);
        if (no == null) return;
        if (!byProductNo.has(no)) byProductNo.set(no, []);
        byProductNo.get(no).push(item.entryId);
      });

      for (const [productNo, entryIds] of byProductNo) {
        if (cancelled) break;
        if (workTabProductFetchInFlightRef.current.has(productNo)) continue;
        workTabProductFetchInFlightRef.current.add(productNo);
        try {
          const details = await getProductDetails(productNo, companyNo);
          const name = (details?.상품명 || details?.ProductName || '').trim();
          if (!name || cancelled) continue;
          setWorkTabs((prev) =>
            prev.map((t) => {
              if (t.kind === 'group') {
                return {
                  ...t,
                  entries: (t.entries || []).map((e) =>
                    entryIds.includes(e.id) && !(e.productDisplayName && String(e.productDisplayName).trim())
                      ? { ...e, productDisplayName: name }
                      : e
                  ),
                };
              }
              return entryIds.includes(t.id) &&
                !(t.productDisplayName && String(t.productDisplayName).trim())
                ? { ...t, productDisplayName: name }
                : t;
            })
          );
        } catch {
          // ignore (network / 상품 없음)
        } finally {
          workTabProductFetchInFlightRef.current.delete(productNo);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workTabs]);

  useEffect(() => {
    const restoreForCurrentTab = () => {
      const store = readDraftStore();
      const draft = store[activeWorkTabId];
      if (!draft) return;
      applyRouteDraft(draft);
    };

    // 비동기로 렌더링되는 화면이 많아 짧은 재시도로 복원 성공률을 높입니다.
    const delays = [0, 180, 600, 1200];
    draftRestoreTimersRef.current = delays.map((delay) => window.setTimeout(restoreForCurrentTab, delay));

    return () => {
      draftRestoreTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      draftRestoreTimersRef.current = [];

      const draft = collectRouteDraft();
      const prevStore = readDraftStore();
      const nextStore = {
        ...prevStore,
        [activeWorkTabId]: {
          ...draft,
          updatedAt: Date.now(),
        },
      };

      const entries = Object.entries(nextStore)
        .sort((a, b) => (a?.[1]?.updatedAt || 0) - (b?.[1]?.updatedAt || 0))
        .slice(-40);
      writeDraftStore(Object.fromEntries(entries));
    };
  }, [activeWorkTabId]);

  const handleOpenWorkTab = (tab) => {
    if (tab.kind === 'group') return;
    navigate(`${tab.path}${tab.search || ''}${tab.hash || ''}`);
  };

  const handleOpenWorkTabEntry = (entry) => {
    navigate(`${entry.path}${entry.search || ''}${entry.hash || ''}`);
    clearWorkTabHoverCloseTimer();
    setOpenWorkTabGroupKey(null);
  };

  const navigateAfterGroupEntryRemoved = (nextTabs, groupKey) => {
    const updatedSame = nextTabs.find((x) => x.kind === 'group' && x.groupKey === groupKey);
    if (updatedSame?.entries?.[0]) {
      const e = updatedSame.entries[0];
      navigate(`${e.path}${e.search || ''}${e.hash || ''}`);
      return;
    }
    const otherGroup = nextTabs.find((x) => x.kind === 'group' && x.entries?.length);
    if (otherGroup?.entries?.[0]) {
      const e = otherGroup.entries[0];
      navigate(`${e.path}${e.search || ''}${e.hash || ''}`);
      return;
    }
    const simpleTab = nextTabs.find((x) => x.kind !== 'group');
    if (simpleTab) {
      navigate(`${simpleTab.path}${simpleTab.search || ''}${simpleTab.hash || ''}`);
    } else {
      navigate('/products/tour');
    }
  };

  const handleRemoveWorkTabGroupEntry = (groupKey, entryId) => {
    setWorkTabs((prev) => {
      const nextTabs = prev
        .map((x) => {
          if (x.kind !== 'group' || x.groupKey !== groupKey) return x;
          const entries = (x.entries || []).filter((e) => e.id !== entryId);
          return entries.length ? { ...x, entries } : null;
        })
        .filter(Boolean);

      const g = nextTabs.find((x) => x.kind === 'group' && x.groupKey === groupKey);
      if (!g || (g.entries?.length || 0) <= 1) {
        window.setTimeout(() => {
          setOpenWorkTabGroupKey(null);
          clearWorkTabHoverCloseTimer();
        }, 0);
      }

      if (entryId === activeWorkTabId) {
        navigateAfterGroupEntryRemoved(nextTabs, groupKey);
      }

      return nextTabs;
    });
  };

  const handleCloseWorkTab = (tab, tabKind) => {
    setWorkTabs((prev) => {
      if (tabKind === 'group') {
        const removeEntryId =
          tab.entries?.find((e) => e.id === activeWorkTabId)?.id ??
          tab.entries?.[tab.entries.length - 1]?.id;
        if (!removeEntryId) return prev;

        const nextTabs = prev
          .map((x) => {
            if (x.kind !== 'group' || x.groupKey !== tab.groupKey) return x;
            const entries = (x.entries || []).filter((e) => e.id !== removeEntryId);
            return entries.length ? { ...x, entries } : null;
          })
          .filter(Boolean);

        if (removeEntryId === activeWorkTabId) {
          navigateAfterGroupEntryRemoved(nextTabs, tab.groupKey);
        }

        return nextTabs;
      }

      const tabId = tab.id;
      const closingIndex = prev.findIndex((t) => t.kind !== 'group' && t.id === tabId);
      if (closingIndex === -1) return prev;

      const nextTabs = prev.filter((x) => x.kind === 'group' || x.id !== tabId);

      if (tabId === activeWorkTabId) {
        const fallbackTab = nextTabs[closingIndex] || nextTabs[closingIndex - 1];
        if (fallbackTab && fallbackTab.kind === 'group') {
          const first = fallbackTab.entries?.[0];
          if (first) navigate(`${first.path}${first.search || ''}${first.hash || ''}`);
          else navigate('/products/tour');
        } else if (fallbackTab && fallbackTab.kind !== 'group') {
          navigate(`${fallbackTab.path}${fallbackTab.search || ''}${fallbackTab.hash || ''}`);
        } else {
          navigate('/products/tour');
        }
      }

      return nextTabs;
    });
  };

  useEffect(() => {
    if (!isAdminUser && isSettingsLauncherOpen) {
      setIsSettingsLauncherOpen(false);
    }
  }, [isAdminUser, isSettingsLauncherOpen]);

  // Keep accordion open when navigating inside products area
  React.useEffect(() => {
    if (isInProductsArea && !productsOpen) setProductsOpen(true);
  }, [isInProductsArea]);

  useEffect(() => {
    if (!isSettingsLauncherOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsSettingsLauncherOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSettingsLauncherOpen]);

  // Close any flyout on route change
  useEffect(() => {
    setActiveFlyout(null);
  }, [location.pathname]);

  useEffect(() => {
    clearWorkTabHoverCloseTimer();
    setOpenWorkTabGroupKey(null);
  }, [location.pathname, location.search, location.hash]);

  useEffect(
    () => () => {
      clearWorkTabHoverCloseTimer();
    },
    []
  );

  useLayoutEffect(() => {
    if (!openWorkTabGroupKey) {
      setWorkTabDropdownPos(null);
      return;
    }
    const el = workTabGroupWrapRefs.current[openWorkTabGroupKey];
    if (!el) return;
    const r = el.getBoundingClientRect();
    setWorkTabDropdownPos({
      top: r.bottom,
      left: r.left,
      minWidth: Math.max(r.width, 240),
    });
  }, [openWorkTabGroupKey, workTabs]);

  useEffect(() => {
    if (!openWorkTabGroupKey) return;
    const update = () => {
      const el = workTabGroupWrapRefs.current[openWorkTabGroupKey];
      if (!el) return;
      const r = el.getBoundingClientRect();
      setWorkTabDropdownPos({
        top: r.bottom,
        left: r.left,
        minWidth: Math.max(r.width, 240),
      });
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [openWorkTabGroupKey]);

  useEffect(() => {
    if (!openWorkTabGroupKey) return;
    const onDown = (e) => {
      const el = e.target;
      if (!(el instanceof Element)) return;
      if (
        el.closest('[data-work-tab-group-wrap]') ||
        el.closest('[data-work-tab-group-portal]')
      ) {
        return;
      }
      setOpenWorkTabGroupKey(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openWorkTabGroupKey]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close global search dropdown and reset search when clicking outside search area
  useEffect(() => {
    const handleClickOutsideSearch = (event) => {
      if (globalSearchRef.current && !globalSearchRef.current.contains(event.target)) {
        // If nothing is active, skip unnecessary state updates
        if (!globalSearchKeyword && (!globalSearchResults || globalSearchResults.length === 0) && !globalSearchError) {
          return;
        }

        if (globalSearchTimerRef.current) {
          clearTimeout(globalSearchTimerRef.current);
          globalSearchTimerRef.current = null;
        }

        setGlobalSearchKeyword('');
        setGlobalSearchResults([]);
        setGlobalSearchError('');
        setIsGlobalSearchLoading(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideSearch);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideSearch);
    };
  }, [globalSearchKeyword, globalSearchResults, globalSearchError]);

  const openSmartGuide = (initialTab = 'price', options = {}) => {
    const { fromTip = false } = options;
    setSmartGuideFromTip(!!fromTip);

    if (!fromTip) {
      try {
        const key = 'trp_smart_guide_pref';
        const raw = window.localStorage.getItem(key);
        setSmartGuideDontShow(raw === 'never');
      } catch (e) {
        setSmartGuideDontShow(false);
      }
    } else {
      // Tip에서 연 경우에는 "앞으로 보지 않기" 옵션을 사용하지 않습니다.
      setSmartGuideDontShow(false);
    }

    setSmartGuideTab(initialTab);
    setIsSmartGuideOpen(true);
  };

  const handleSmartGuideClose = () => {
    if (!smartGuideFromTip) {
      try {
        const key = 'trp_smart_guide_pref';
        if (smartGuideDontShow) {
          window.localStorage.setItem(key, 'never');
        } else {
          window.localStorage.removeItem(key);
        }
      } catch (e) {
        // ignore
      }
    }
    setIsSmartGuideOpen(false);
    setSmartGuideFromTip(false);
  };

  const fetchCompanyStaffDetail = async (targetUserNo) => {
    const safeTargetUserNo = Number(targetUserNo);
    if (!Number.isFinite(safeTargetUserNo)) {
      throw new Error('유저번호가 올바르지 않습니다.');
    }

    const { companyNo } = getUserInfo() || {};
    const resolvedCompanyNo = companyNo ? String(companyNo) : '1';

    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: resolvedCompanyNo, 리스트값: null },
        { 컬럼명: '@유저번호', 값: safeTargetUserNo, 리스트값: null },
      ],
    };

    const url = `${API_URL}?procedureName=DBO.CW_S_%EC%82%AC%EC%9B%90%EC%A0%95%EB%B3%B4`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error('사원 상세 정보를 불러오지 못했습니다.');
    }

    const data = await res.json().catch(() => null);
    const row = data && data.table && data.table[0];
    if (!row) {
      throw new Error('사원 상세 정보를 찾을 수 없습니다.');
    }
    return row;
  };

  const openCompanyStaffEditor = async (row) => {
    const targetUserNo = row && row.유저번호;
    if (!targetUserNo) {
      setCompanyStaffSaveError('유저번호가 없어 편집할 수 없습니다.');
      return;
    }

    setCompanyStaffEditorMode('edit');
    setSelectedCompanyStaffRow(row);
    setSelectedCompanyStaffDetail(null);
    setCompanyStaffSaveError('');
    setIsLoadingCompanyStaffDetail(true);

    try {
      const detail = await fetchCompanyStaffDetail(targetUserNo);
      setSelectedCompanyStaffDetail(detail);
      setCompanyStaffEditForm({
        isAdmin: !!detail.관리자여부,
        isSettlementAdmin: !!detail.정산관리자여부,
        isEmployed: detail.재직여부 != null ? !!detail.재직여부 : true,
        nameKr: detail.한글이름 || detail.유저명 || row?.유저명 || '',
      });
    } catch (e) {
      setCompanyStaffSaveError(e && e.message ? e.message : '사원 상세 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoadingCompanyStaffDetail(false);
    }
  };

  const closeCompanyStaffEditor = () => {
    setCompanyStaffEditorMode(null);
    setSelectedCompanyStaffRow(null);
    setSelectedCompanyStaffDetail(null);
    setCompanyStaffSaveError('');
    setIsLoadingCompanyStaffDetail(false);
    setIsSavingCompanyStaff(false);
  };

  const openCompanyStaffCreator = () => {
    setCompanyStaffEditorMode('create');
    setSelectedCompanyStaffRow(null);
    setSelectedCompanyStaffDetail(null);
    setIsLoadingCompanyStaffDetail(false);
    setCompanyStaffSaveError('');
    setCompanyStaffCreateForm({ loginId: '', nameKr: '', phone: '', isAdmin: false, isSettlementAdmin: false });
  };

  const handleCreateCompanyStaff = async () => {
    try {
      const loginId = String(companyStaffCreateForm.loginId || '').trim();
      const nameKr = String(companyStaffCreateForm.nameKr || '').trim();
      const phone = String(companyStaffCreateForm.phone || '').trim();
      const isAdmin = !!companyStaffCreateForm.isAdmin;
      const isSettlementAdmin = !!companyStaffCreateForm.isSettlementAdmin;

      if (!loginId) {
        setCompanyStaffSaveError('아이디(이메일)를 입력해 주세요.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginId)) {
        setCompanyStaffSaveError('아이디는 이메일 형식으로 입력해 주세요.');
        return;
      }
      if (!nameKr) {
        setCompanyStaffSaveError('한글이름을 입력해 주세요.');
        return;
      }
      if (!phone) {
        setCompanyStaffSaveError('휴대폰을 입력해 주세요.');
        return;
      }

      const { companyNo, userNo: changerUserNo } = getUserInfo() || {};
      const resolvedCompanyNo = companyNo ? String(companyNo) : '1';

      setIsSavingCompanyStaff(true);
      setCompanyStaffSaveError('');

      const payload = {
        Table1: [
          { 컬럼명: '@유저번호', 값: 'NULL', 리스트값: null },
          { 컬럼명: '@업체번호', 값: resolvedCompanyNo, 리스트값: null },
          { 컬럼명: '@재직여부', 값: true, 리스트값: null },
          { 컬럼명: '@관리자여부', 값: isAdmin, 리스트값: null },
          { 컬럼명: '@수납관리자여부', 값: false, 리스트값: null },
          { 컬럼명: '@정산관리자여부', 값: isSettlementAdmin, 리스트값: null },
          { 컬럼명: '@발권담당자여부', 값: false, 리스트값: null },
          { 컬럼명: '@통장조회가능여부', 값: false, 리스트값: null },
          { 컬럼명: '@아이디', 값: loginId, 리스트값: null },
          { 컬럼명: '@한글이름', 값: nameKr, 리스트값: null },
          { 컬럼명: '@영문성', 값: '', 리스트값: null },
          { 컬럼명: '@영문이름', 값: '', 리스트값: null },
          { 컬럼명: '@이메일', 값: '', 리스트값: null },
          { 컬럼명: '@휴대폰', 값: phone, 리스트값: null },
          { 컬럼명: '@부서명', 값: '', 리스트값: null },
          { 컬럼명: '@부서번호', 값: 'NULL', 리스트값: null },
          { 컬럼명: '@직책명', 값: '', 리스트값: null },
          { 컬럼명: '@입사일', 값: 'NULL', 리스트값: null },
          { 컬럼명: '@생일', 값: 'NULL', 리스트값: null },
          { 컬럼명: '@우편번호', 값: '', 리스트값: null },
          { 컬럼명: '@기본주소', 값: '', 리스트값: null },
          { 컬럼명: '@상세주소', 값: '', 리스트값: null },
          { 컬럼명: '@성별코드', 값: '031001', 리스트값: null },
          { 컬럼명: '@변경자', 값: changerUserNo != null ? String(changerUserNo) : '', 리스트값: null },
          { 컬럼명: '@프로필', 값: '', 리스트값: null },
          { 컬럼명: '@역할목록', 값: '[]', 리스트값: null },
        ],
      };

      const url = `${API_URL}?procedureName=DBO.TRP_T_%EC%82%AC%EC%9B%90%EB%93%B1%EB%A1%9D`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let serverMessage = '';
        const fallbackMessage = '사원 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.';
        try {
          const errData = await res.json().catch(() => null);
          const table1 = (errData && (errData.table1 || errData.Table1 || errData.table || errData.Table)) || null;
          if (Array.isArray(table1) && table1[0] && table1[0].message != null) {
            serverMessage = String(table1[0].message);
          } else if (errData && errData.message != null) {
            serverMessage = String(errData.message);
          }
        } catch (_) {
          serverMessage = '';
        }
        setCompanyStaffSaveError(serverMessage || fallbackMessage);
        return;
      }

      await res.json().catch(() => null);
      await fetchCompanyStaffList();
      closeCompanyStaffEditor();
      try {
        window.alert('사원이 정상적으로 등록되었습니다.');
      } catch (_) {
      }
    } catch (e) {
      console.error('Failed to create company staff:', e);
      setCompanyStaffSaveError('사원 등록 중 오류가 발생했습니다.');
    } finally {
      setIsSavingCompanyStaff(false);
    }
  };

  const handleSaveCompanyStaff = async () => {
    if (!selectedCompanyStaffRow || !selectedCompanyStaffDetail) {
      return;
    }

    try {
      const { companyNo, userNo: changerUserNo } = getUserInfo() || {};
      const resolvedCompanyNo = companyNo ? String(companyNo) : '1';
      const targetUserNo = Number(selectedCompanyStaffDetail.유저번호 || selectedCompanyStaffRow.유저번호);
      if (!Number.isFinite(targetUserNo)) {
        setCompanyStaffSaveError('유저번호가 올바르지 않습니다.');
        return;
      }

      const nameKr = String(companyStaffEditForm.nameKr || '').trim();
      if (!nameKr) {
        setCompanyStaffSaveError('사원명을 입력해 주세요.');
        return;
      }

      setIsSavingCompanyStaff(true);
      setCompanyStaffSaveError('');

      const base = selectedCompanyStaffDetail || {};

      const rawProfile = base.프로필 || '';
      const normalizedProfile = normalizeProfilePath(rawProfile, resolvedCompanyNo);

      const rolesValue = base.역할목록 != null ? base.역할목록 : (base.역할 != null ? base.역할 : '[]');
      const rolesString = typeof rolesValue === 'string' ? rolesValue : JSON.stringify(rolesValue);

      const payload = {
        Table1: [
          { 컬럼명: '@유저번호', 값: targetUserNo, 리스트값: null },
          { 컬럼명: '@업체번호', 값: resolvedCompanyNo, 리스트값: null },
          { 컬럼명: '@재직여부', 값: !!companyStaffEditForm.isEmployed, 리스트값: null },
          { 컬럼명: '@관리자여부', 값: !!companyStaffEditForm.isAdmin, 리스트값: null },
          { 컬럼명: '@수납관리자여부', 값: base.수납관리자여부 != null ? !!base.수납관리자여부 : false, 리스트값: null },
          { 컬럼명: '@정산관리자여부', 값: !!companyStaffEditForm.isSettlementAdmin, 리스트값: null },
          { 컬럼명: '@발권담당자여부', 값: base.발권담당자여부 != null ? !!base.발권담당자여부 : false, 리스트값: null },
          { 컬럼명: '@통장조회가능여부', 값: base.통장조회가능여부 != null ? !!base.통장조회가능여부 : false, 리스트값: null },
          { 컬럼명: '@아이디', 값: base.아이디 || '', 리스트값: null },
          { 컬럼명: '@한글이름', 값: nameKr, 리스트값: null },
          { 컬럼명: '@영문성', 값: base.영문성 || '', 리스트값: null },
          { 컬럼명: '@영문이름', 값: base.영문이름 || '', 리스트값: null },
          { 컬럼명: '@이메일', 값: base.이메일 || base.대표이메일 || base.아이디 || '', 리스트값: null },
          { 컬럼명: '@휴대폰', 값: base.휴대폰 || base.대표전화 || '', 리스트값: null },
          { 컬럼명: '@부서명', 값: base.부서명 != null ? base.부서명 : null, 리스트값: null },
          { 컬럼명: '@부서번호', 값: base.부서번호 != null ? base.부서번호 : 'NULL', 리스트값: null },
          { 컬럼명: '@직책명', 값: base.직책명 || '', 리스트값: null },
          { 컬럼명: '@입사일', 값: base.입사일 || null, 리스트값: null },
          { 컬럼명: '@생일', 값: base.생일 != null ? base.생일 : 'NULL', 리스트값: null },
          { 컬럼명: '@우편번호', 값: base.우편번호 || '', 리스트값: null },
          { 컬럼명: '@기본주소', 값: base.기본주소 || '', 리스트값: null },
          { 컬럼명: '@상세주소', 값: base.상세주소 || '', 리스트값: null },
          { 컬럼명: '@성별코드', 값: base.성별코드 || '031001', 리스트값: null },
          { 컬럼명: '@변경자', 값: changerUserNo != null ? String(changerUserNo) : '', 리스트값: null },
          { 컬럼명: '@프로필', 값: normalizedProfile || '', 리스트값: null },
          { 컬럼명: '@역할목록', 값: rolesString || '[]', 리스트값: null },
        ],
      };

      const url = `${API_URL}?procedureName=DBO.TRP_T_%EC%82%AC%EC%9B%90%EC%88%98%EC%A0%95`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        setCompanyStaffSaveError('사원 정보 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      await res.json().catch(() => null);
      await fetchCompanyStaffList();
      closeCompanyStaffEditor();
      try {
        window.alert('사원 정보가 정상적으로 저장되었습니다.');
      } catch (_) {
      }
    } catch (e) {
      console.error('Failed to save company staff:', e);
      setCompanyStaffSaveError('사원 정보 저장 중 오류가 발생했습니다.');
    } finally {
      setIsSavingCompanyStaff(false);
    }
  };

  useEffect(() => {
    const RSS_URL = 'https://www.traveltimes.co.kr/rss/allArticle.xml';

    const parseRssXml = (text) => {
      const parser = new window.DOMParser();
      const xml = parser.parseFromString(text, 'application/xml');
      return Array.from(xml.getElementsByTagName('item'))
        .map((item) => {
          const titleNode = item.getElementsByTagName('title')[0];
          const linkNode = item.getElementsByTagName('link')[0];
          const title = titleNode?.textContent?.trim() || '';
          const link = linkNode?.textContent?.trim() || '';
          return title && link ? { title, link } : null;
        })
        .filter(Boolean);
    };

    const fetchRss = async () => {
      // Primary: rss2json (CORS-friendly JSON). Fallback: allorigins XML proxy.
      try {
        const jsonUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(RSS_URL)}`;
        const response = await fetch(jsonUrl);
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'ok' && Array.isArray(data.items)) {
            const items = data.items
              .map((item) => {
                const title = item.title?.trim() || '';
                const link = item.link?.trim() || '';
                return title && link ? { title, link } : null;
              })
              .filter(Boolean);
            if (items.length > 0) {
              setRssItems(items);
              setCurrentRssIndex(0);
              return;
            }
          }
        }
      } catch (error) {
        console.warn('rss2json fetch failed, trying proxy fallback:', error);
      }

      try {
        const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(RSS_URL)}`;
        const response = await fetch(proxyUrl);
        if (!response.ok) {
          return;
        }
        const items = parseRssXml(await response.text());
        if (items.length > 0) {
          setRssItems(items);
          setCurrentRssIndex(0);
        }
      } catch (error) {
        console.error('Failed to fetch RSS:', error);
      }
    };

    fetchRss();
  }, []);

  useEffect(() => {
    if (!rssItems || rssItems.length === 0) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setCurrentRssIndex((prev) => {
        const next = (prev + 1) % rssItems.length;
        return next;
      });
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [rssItems]);

  const clearFlyoutTimer = () => {
    if (flyoutTimerRef.current) {
      clearTimeout(flyoutTimerRef.current);
      flyoutTimerRef.current = null;
    }
  };

  // ===== 통합 검색 로직 =====
  const globalSearchTimerRef = useRef(null);

  const normalizeString = (v) => {
    if (v == null) return '';
    const s = String(v);
    const normalized = typeof s.normalize === 'function' ? s.normalize('NFKC') : s;
    return normalized
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/[［]/g, '[')
      .replace(/[］]/g, ']')
      .trim();
  };

  const normalizeStringLower = (v) => normalizeString(v).toLowerCase();

  const matchesKeyword = (keyword, fields) => {
    const kw = normalizeStringLower(keyword);
    if (!kw) return false;
    return fields.some((field) => normalizeStringLower(field).includes(kw));
  };

  const runProductSearch = async (keyword) => {
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        return [];
      }
      const list = await getProductList(companyNo, userNo).catch(() => []);
      const arr = Array.isArray(list) ? list : [];
      const filtered = arr.filter((row) => {
        const fields = [
          row?.상품번호,
          row?.상품명,
          row?.도시,
          row?.국가,
          row?.상품코드,
          row?.상품담당,
          row?.상품담당자,
        ];
        return matchesKeyword(keyword, fields);
      });
      return filtered.slice(0, 20).map((row) => ({
        type: 'product',
        id: row?.상품번호,
        label: normalizeString(row?.상품명) || '(상품명 없음)',
        subLabel: [normalizeString(row?.도시), normalizeString(row?.국가)].filter(Boolean).join(' / '),
        extra: normalizeString(row?.상품담당 || row?.상품담당자),
        raw: row,
      }));
    } catch (e) {
      console.error('Global product search failed:', e);
      throw e;
    }
  };

  const runReservationSearch = async (keyword) => {
    try {
      const list = await getReservationTravelerList().catch(() => []);
      const arr = Array.isArray(list) ? list : [];
      const filtered = arr.filter((row) => {
        const fields = [
          row?.판매상품명,
          row?.한글이름,
          row?.영문성,
          row?.영문이름,
          row?.여행객비고,
          row?.휴대폰,
          row?.이메일,
          row?.대리점,
          row?.주문자,
          row?.주문자연락처,
        ];
        return matchesKeyword(keyword, fields);
      });
      return filtered.slice(0, 20).map((row) => {
        const orderNo = row?.주문번호 ?? row?.대표주문번호 ?? null;
        const saleNo = row?.판매번호 ?? row?.주문판매번호 ?? null;
        return {
          type: 'reservation',
          orderNo,
          saleNo,
          label: [normalizeString(row?.한글이름), normalizeString(row?.판매상품명)].filter(Boolean).join(' - '),
          subLabel: [normalizeString(row?.휴대폰), normalizeString(row?.이메일), normalizeString(row?.대리점)].filter(Boolean).join(' / '),
          raw: row,
        };
      });
    } catch (e) {
      console.error('Global reservation search failed:', e);
      throw e;
    }
  };

  const runConsultSearch = async (keyword) => {
    try {
      const list = await getConsultList().catch(() => []);
      const arr = Array.isArray(list) ? list : [];
      const filtered = arr.filter((row) => {
        const fields = [
          row?.요청자명,
          row?.요청자휴대폰,
          row?.요청자이메일,
          row?.견적명,
          row?.견적내용,
        ];
        return matchesKeyword(keyword, fields);
      });
      return filtered.slice(0, 20).map((row) => {
        const rawTeamColor = row?.팀컬러;
        const digits = rawTeamColor != null ? String(rawTeamColor).replace(/[^0-9]/g, '') : '';
        const teamColorNumeric = digits ? Number(digits) : null;
        const consultNo = row?.상담번호 != null ? Number(row.상담번호) : null;
        return {
          type: 'consult',
          consultNo,
          teamColor: teamColorNumeric,
          label: normalizeString(row?.견적명) || '(견적명 없음)',
          subLabel: [normalizeString(row?.요청자명), normalizeString(row?.요청자휴대폰), normalizeString(row?.요청자이메일)].filter(Boolean).join(' / '),
          raw: row,
        };
      });
    } catch (e) {
      console.error('Global consult search failed:', e);
      throw e;
    }
  };

  const handleSelectGlobalSearchResult = (item) => {
    if (!item) return;
    try {
      if (item.type === 'product') {
        // 상품: 상품관리 리스트 화면으로 이동 (상품종류에 따라 투어/호텔 분기)
        const raw = item?.raw || {};
        const productKindCode = raw?.상품종류코드 != null ? String(raw.상품종류코드) : '';
        const productKindName = raw?.상품종류 != null ? String(raw.상품종류) : '';
        const isHotelProduct = productKindCode === '001001' || productKindName.includes('호텔');

        navigate(isHotelProduct ? '/products/hotel' : '/products/tour', {
          state: {
            focusProductNo: item.id,
          },
        });
      } else if (item.type === 'reservation') {
        const orderNo = item.orderNo;
        if (!orderNo) return;
        navigate(`/reservation-detail/${orderNo}`, {
          state: {
            saleNo: item.saleNo ?? null,
            productName: item.raw?.판매상품명 ?? undefined,
          },
        });
      } else if (item.type === 'consult') {
        const code = '-';
        navigate(`/quote-detail/${encodeURIComponent(code)}`, {
          state: {
            productName: item.raw?.견적명 ?? '',
            productNo: item.teamColor ?? null,
            consultNo: item.consultNo ?? null,
          },
        });
      }
    } finally {
      setGlobalSearchResults([]);
    }
  };

  useEffect(() => {
    const keyword = normalizeString(globalSearchKeyword);
    if (globalSearchTimerRef.current) {
      clearTimeout(globalSearchTimerRef.current);
      globalSearchTimerRef.current = null;
    }

    if (!keyword) {
      setGlobalSearchResults([]);
      setGlobalSearchError('');
      setIsGlobalSearchLoading(false);
      return undefined;
    }

    // 간단한 디바운스: 300ms 대기 후 검색 실행
    globalSearchTimerRef.current = setTimeout(async () => {
      try {
        setIsGlobalSearchLoading(true);
        setGlobalSearchError('');
        let results = [];
        if (globalSearchDomain === 'product') {
          results = await runProductSearch(keyword);
        } else if (globalSearchDomain === 'reservation') {
          results = await runReservationSearch(keyword);
        } else if (globalSearchDomain === 'consult') {
          results = await runConsultSearch(keyword);
        }
        setGlobalSearchResults(results || []);
      } catch (e) {
        setGlobalSearchError('검색 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
        setGlobalSearchResults([]);
      } finally {
        setIsGlobalSearchLoading(false);
      }
    }, 300);

    return () => {
      if (globalSearchTimerRef.current) {
        clearTimeout(globalSearchTimerRef.current);
        globalSearchTimerRef.current = null;
      }
    };
  }, [globalSearchDomain, globalSearchKeyword]);

  const scheduleFlyoutClose = () => {
    clearFlyoutTimer();
    flyoutTimerRef.current = setTimeout(() => {
      setActiveFlyout(null);
    }, 500);
  };

  // Fetch user profile (회사/유저 기본 정보) + 사용자 상세 정보
  const fetchUserProfileAndStaffInfo = async () => {
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        return;
      }

      // 1) 회사/유저 프로필 정보 (회사명, 유저명, 로고)
      const profilePayload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
          { 컬럼명: '@유저번호', 값: Number(userNo), 리스트값: null },
        ],
      };
      const profileUrl = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%9C%A0%EC%A0%80%EC%A0%95%EB%B3%B4';
      const profileResponse = await fetch(profileUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(profilePayload),
      });
      if (profileResponse.ok) {
        const data = await profileResponse.json();
        const profileData = data?.table?.[0];
        if (profileData) {
          setUserProfile({
            companyName: profileData.업체명 || '',
            userName: profileData.유저명 || '',
            companyLogo: profileData.회사로고 || '',
          });
        }
      }

      // 2) 사원 상세 정보 (아이디, 이메일, 휴대폰, 한글이름, 생년월일)
      const staffPayload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo) },
          { 컬럼명: '@유저번호', 값: Number(userNo) },
        ],
      };
      const staffUrl = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%82%AC%EC%9B%90%EC%A0%95%EB%B3%B4';
      const staffResponse = await fetch(staffUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(staffPayload),
      });
      if (staffResponse.ok) {
        const data = await staffResponse.json();
        const staff = data?.table?.[0];
        if (staff) {
          setOriginalStaffInfo(staff);
          setUserInfo({
            name: staff.한글이름 || staff.유저명 || '',
            email: staff.이메일 || staff.대표이메일 || staff.아이디 || '',
            phone: staff.휴대폰 || staff.대표전화 || '',
            birthDate: staff.생년월일 || '',
            loginId: staff.아이디 || '',
            profile: staff.프로필 || '',
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch user profile or staff info:', error);
    }
  };

  useEffect(() => {
    fetchUserProfileAndStaffInfo();
  }, []);

  useEffect(() => {
    if (!isAdminUser && isCompanyModalOpen) {
      setIsCompanyModalOpen(false);
    }
  }, [isAdminUser, isCompanyModalOpen]);

  const handleUserInfoFieldChange = (field, value) => {
    setUserInfo((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const handleSaveUserInfo = async () => {
    if (!userInfo || !originalStaffInfo) {
      return;
    }

    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        setUserInfoSaveError('로그인 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.');
        return;
      }

      setIsSavingUserInfo(true);
      setUserInfoSaveError('');

      const base = originalStaffInfo || {};

      const rawProfile = base.프로필 || '';
      let normalizedProfile = rawProfile;
      if (typeof normalizedProfile === 'string' && normalizedProfile) {
        // 1) 전체 URL인 경우 도메인 제거
        const originPrefix = 'https://kr.object.iwinv.kr/orgin';
        if (normalizedProfile.startsWith(originPrefix)) {
          normalizedProfile = normalizedProfile.slice(originPrefix.length);
        }
        // 2) '/Profile/파일명' 또는 '1/Profile/파일명' 등에서 파일명만 추출
        const profileIndex = normalizedProfile.lastIndexOf('/Profile/');
        if (profileIndex !== -1) {
          const filePart = normalizedProfile.slice(profileIndex + '/Profile/'.length);
          if (filePart) {
            normalizedProfile = `${companyNo}/Profile/${filePart}`;
          }
        }
      }

      const merged = {
        ...base,
        아이디: (userInfo.loginId != null && userInfo.loginId !== '') ? userInfo.loginId : (base.아이디 || ''),
        한글이름: (userInfo.name != null && userInfo.name !== '') ? userInfo.name : (base.한글이름 || base.유저명 || ''),
        이메일: (userInfo.email != null && userInfo.email !== '')
          ? userInfo.email
          : (base.이메일 || base.대표이메일 || base.아이디 || ''),
        휴대폰: (userInfo.phone != null && userInfo.phone !== '')
          ? userInfo.phone
          : (base.휴대폰 || base.대표전화 || ''),
        생년월일: (userInfo.birthDate != null && userInfo.birthDate !== '')
          ? userInfo.birthDate
          : (base.생년월일 || null),
        프로필: normalizedProfile,
      };

      const table1 = [
        { 컬럼명: '@유저번호', 값: Number(userNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@재직여부', 값: merged.재직여부 != null ? merged.재직여부 : true },
        { 컬럼명: '@관리자여부', 값: merged.관리자여부 != null ? merged.관리자여부 : false },
        { 컬럼명: '@수납관리자여부', 값: merged.수납관리자여부 != null ? merged.수납관리자여부 : false },
        { 컬럼명: '@정산관리자여부', 값: merged.정산관리자여부 != null ? merged.정산관리자여부 : false },
        { 컬럼명: '@발권담당자여부', 값: merged.발권담당자여부 != null ? merged.발권담당자여부 : false },
        { 컬럼명: '@통장조회가능여부', 값: merged.통장조회가능여부 != null ? merged.통장조회가능여부 : null },
        { 컬럼명: '@아이디', 값: merged.아이디 || '' },
        { 컬럼명: '@한글이름', 값: merged.한글이름 || '' },
        { 컬럼명: '@영문성', 값: merged.영문성 || '' },
        { 컬럼명: '@영문이름', 값: merged.영문이름 || '' },
        { 컬럼명: '@이메일', 값: merged.이메일 || '' },
        { 컬럼명: '@휴대폰', 값: merged.휴대폰 || '' },
        { 컬럼명: '@부서명', 값: merged.부서명 != null ? merged.부서명 : null },
        { 컬럼명: '@부서번호', 값: merged.부서번호 != null ? merged.부서번호 : 'NULL' },
        { 컬럼명: '@직책명', 값: merged.직책명 || '' },
        { 컬럼명: '@입사일', 값: merged.입사일 || null },
        { 컬럼명: '@생일', 값: merged.생일 != null ? merged.생일 : 'NULL' },
        { 컬럼명: '@우편번호', 값: merged.우편번호 || '' },
        { 컬럼명: '@기본주소', 값: merged.기본주소 || '' },
        { 컬럼명: '@상세주소', 값: merged.상세주소 || '' },
        { 컬럼명: '@성별코드', 값: merged.성별코드 || '031001' },
        { 컬럼명: '@변경자', 값: String(userNo) },
        { 컬럼명: '@프로필', 값: merged.프로필 || '' },
        { 컬럼명: '@역할목록', 값: merged.역할목록 || '[]' },
      ];

      const payload = { Table1: table1 };

      const url = `${API_URL}?procedureName=DBO.TRP_T_%EC%82%AC%EC%9B%90%EC%88%98%EC%A0%95_%EA%B0%9C%EB%B3%84`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setUserInfoSaveError('사용자 정보 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      const data = await response.json().catch(() => null);
      const updated = data && data.table && data.table[0];
      if (updated) {
        setOriginalStaffInfo(updated);
        setUserInfo({
          name: updated.유저명 || updated.한글이름 || '',
          email: updated.대표이메일 || updated.이메일 || updated.아이디 || '',
          phone: updated.대표전화 || updated.휴대폰 || '',
          birthDate: updated.생년월일 || '',
          loginId: updated.아이디 || '',
          profile: updated.프로필 || '',
        });
        setUserProfile((prev) => ({
          ...(prev || {}),
          userName: updated.유저명 || updated.한글이름 || (prev && prev.userName) || '',
        }));
        setIsEditingProfile(false);
        setIsUserInfoModalOpen(false);
        try {
          window.alert('사용자 정보가 정상적으로 저장되었습니다.');
        } catch (_) {
          // window가 없는 환경은 무시
        }
      }
    } catch (error) {
      console.error('Failed to save user info:', error);
      setUserInfoSaveError('사용자 정보 저장 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsSavingUserInfo(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const arrayBuffer = reader.result;
        if (!(arrayBuffer instanceof ArrayBuffer)) {
          reject(new Error('Invalid file data'));
          return;
        }
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        resolve(btoa(binary));
      };
      reader.onerror = () => {
        reject(reader.error || new Error('Failed to read file'));
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const formatBusinessNo = (value) => {
    if (!value) return '';
    const digits = String(value).replace(/[^0-9]/g, '');
    if (digits.length !== 10) return value;
    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 5);
    const part3 = digits.slice(5);
    return `${part1}-${part2}-${part3}`;
  };

  const normalizeProfilePath = (rawProfile, companyNoValue) => {
    const companyNoStr = companyNoValue ? String(companyNoValue) : '';
    if (!rawProfile || typeof rawProfile !== 'string') return '';

    let normalized = rawProfile;
    const originPrefix = 'https://kr.object.iwinv.kr/orgin';
    if (normalized.startsWith(originPrefix)) {
      normalized = normalized.slice(originPrefix.length);
    }

    const profileIndex = normalized.lastIndexOf('/Profile/');
    if (profileIndex !== -1) {
      const filePart = normalized.slice(profileIndex + '/Profile/'.length);
      if (filePart && companyNoStr) {
        normalized = `${companyNoStr}/Profile/${filePart}`;
      }
    }

    return normalized;
  };

  const fetchCompanyInfo = async () => {
    try {
      const { companyNo } = getUserInfo() || {};
      if (!companyNo) {
        return;
      }

      setIsLoadingCompanyInfo(true);

      const payload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        ],
      };

      const url = `${API_URL}?procedureName=DBO.RCT_S_%ED%9A%8C%EC%82%AC%EA%B8%B0%EB%B3%B8%EC%A0%95%EB%B3%B4`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const logoInfo = (data && data.table && data.table[0]) || {};
      const basicInfo = (data && data.table1 && data.table1[0]) || {};

      const nextInfo = {
        companyLogo: logoInfo['회사로고'] || '',
        businessNo: basicInfo['사업자등록번호'] || '',
        companyName: basicInfo['업체명'] || '',
        ceoName: basicInfo['대표자'] || '',
        phone: basicInfo['대표전화'] || '',
        email: basicInfo['대표이메일'] || '',
        zipCode: basicInfo['우편번호'] || '',
        address1: basicInfo['기본주소'] || '',
        address2: basicInfo['상세주소'] || '',
      };

      setCompanyInfo(nextInfo);
      setOriginalCompanyInfo(nextInfo);
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    } finally {
      setIsLoadingCompanyInfo(false);
    }
  };

  const fetchCompanyStaffList = async () => {
    try {
      const { companyNo } = getUserInfo() || {};
      const resolvedCompanyNo = companyNo ? String(companyNo) : '1';

      setIsLoadingCompanyStaffList(true);
      setCompanyStaffListError('');

      const payload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: resolvedCompanyNo, 리스트값: null },
          { 컬럼명: '@상태', 값: 'NULL', 리스트값: null },
        ],
      };

      const url = `${API_URL}?procedureName=DBO.CW_S_%EC%82%AC%EC%9B%90`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setCompanyStaffList([]);
        setCompanyStaffListError('사원 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      const data = await response.json().catch(() => null);
      const list = (data && (data.table || data.Table)) || [];
      setCompanyStaffList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to fetch company staff list:', error);
      setCompanyStaffList([]);
      setCompanyStaffListError('사원 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoadingCompanyStaffList(false);
    }
  };

  const handleLogoFileChange = async (event) => {
    const file = event.target && event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        setLogoUploadError('로그인 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.');
        return;
      }

      setLogoUploadError('');
      setIsUploadingLogo(true);

      const base64File = await fileToBase64(file);

      const uploadPayload = {
        File: base64File,
        FileName: file.name,
        FilePath: `Logo/${companyNo}/`,
      };

      const uploadResponse = await fetch('https://trpliveapi1.tripbox.co.kr/Upload/uploadS3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(uploadPayload),
      });

      if (!uploadResponse.ok) {
        setLogoUploadError('로고 업로드에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      const uploadData = await uploadResponse.json();
      const logoPath = `${uploadPayload.FilePath}${uploadPayload.FileName}`;

      const spPayload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
          { 컬럼명: '@로고경로', 값: logoPath, 리스트값: null },
          { 컬럼명: '@변경자', 값: String(userNo), 리스트값: null },
        ],
      };

      const spUrl = `${API_URL}?procedureName=DBO.RCT_T_%ED%9A%8C%EC%82%AC%EC%A0%95%EB%B3%B4_%EB%A1%9C%EA%B3%A0`;
      const spResponse = await fetch(spUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(spPayload),
      });

      if (!spResponse.ok) {
        setLogoUploadError('로고 경로 저장에 실패했습니다. 다시 시도해 주세요.');
        return;
      }

      const logoUrl = (uploadData && uploadData.url) || `https://kr.object.iwinv.kr/orgin/${logoPath}`;

      setCompanyInfo((prev) => ({
        ...(prev || {}),
        companyLogo: logoUrl,
      }));
      setUserProfile((prev) => ({
        ...(prev || {}),
        companyLogo: logoUrl,
      }));
    } catch (error) {
      console.error('Failed to upload company logo:', error);
      setLogoUploadError('로고 업로드 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsUploadingLogo(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleProfileFileChange = async (event) => {
    const file = event.target && event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        setProfileUploadError('로그인 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.');
        return;
      }

      setProfileUploadError('');
      setIsUploadingProfile(true);

      const base64File = await fileToBase64(file);

      // 파일명 중복 방지를 위해 업로드 시점의 년월일시분초를 suffix로 부여
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const dotIndex = file.name.lastIndexOf('.');
      const baseName = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
      const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : '';
      const uniqueFileName = `${baseName}_${timestamp}${ext}`;

      const uploadPayload = {
        File: base64File,
        FileName: uniqueFileName,
        FilePath: `${companyNo}/Profile/`,
      };

      const uploadResponse = await fetch('https://trpliveapi1.tripbox.co.kr/Upload/uploadS3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(uploadPayload),
      });

      if (!uploadResponse.ok) {
        setProfileUploadError('프로필 이미지 업로드에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      const uploadData = await uploadResponse.json();
      const s3FullPath = `${uploadPayload.FilePath}${uploadPayload.FileName}`;
      const profilePathForDb = `${companyNo}/Profile/${uploadPayload.FileName}`;
      const profileUrl = (uploadData && uploadData.url) || `https://kr.object.iwinv.kr/orgin/${s3FullPath}`;

      if (!originalStaffInfo) {
        setUserInfo((prev) => ({
          ...(prev || {}),
          profile: profileUrl,
        }));
        return;
      }

      const base = originalStaffInfo || {};

      const merged = {
        ...base,
        아이디: (userInfo && userInfo.loginId != null && userInfo.loginId !== '') ? userInfo.loginId : (base.아이디 || ''),
        한글이름: (userInfo && userInfo.name != null && userInfo.name !== '') ? userInfo.name : (base.한글이름 || base.유저명 || ''),
        이메일: (userInfo && userInfo.email != null && userInfo.email !== '')
          ? userInfo.email
          : (base.이메일 || base.대표이메일 || base.아이디 || ''),
        휴대폰: (userInfo && userInfo.phone != null && userInfo.phone !== '')
          ? userInfo.phone
          : (base.휴대폰 || base.대표전화 || ''),
        생년월일: (userInfo && userInfo.birthDate != null && userInfo.birthDate !== '')
          ? userInfo.birthDate
          : (base.생년월일 || null),
        프로필: profilePathForDb,
      };

      const table1 = [
        { 컬럼명: '@유저번호', 값: Number(userNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@재직여부', 값: merged.재직여부 != null ? merged.재직여부 : true },
        { 컬럼명: '@관리자여부', 값: merged.관리자여부 != null ? merged.관리자여부 : false },
        { 컬럼명: '@수납관리자여부', 값: merged.수납관리자여부 != null ? merged.수납관리자여부 : false },
        { 컬럼명: '@정산관리자여부', 값: merged.정산관리자여부 != null ? merged.정산관리자여부 : false },
        { 컬럼명: '@발권담당자여부', 값: merged.발권담당자여부 != null ? merged.발권담당자여부 : false },
        { 컬럼명: '@통장조회가능여부', 값: merged.통장조회가능여부 != null ? merged.통장조회가능여부 : null },
        { 컬럼명: '@아이디', 값: merged.아이디 || '' },
        { 컬럼명: '@한글이름', 값: merged.한글이름 || '' },
        { 컬럼명: '@영문성', 값: merged.영문성 || '' },
        { 컬럼명: '@영문이름', 값: merged.영문이름 || '' },
        { 컬럼명: '@이메일', 값: merged.이메일 || '' },
        { 컬럼명: '@휴대폰', 값: merged.휴대폰 || '' },
        { 컬럼명: '@부서명', 값: merged.부서명 != null ? merged.부서명 : null },
        { 컬럼명: '@부서번호', 값: merged.부서번호 != null ? merged.부서번호 : 'NULL' },
        { 컬럼명: '@직책명', 값: merged.직책명 || '' },
        { 컬럼명: '@입사일', 값: merged.입사일 || null },
        { 컬럼명: '@생일', 값: merged.생일 != null ? merged.생일 : 'NULL' },
        { 컬럼명: '@우편번호', 값: merged.우편번호 || '' },
        { 컬럼명: '@기본주소', 값: merged.기본주소 || '' },
        { 컬럼명: '@상세주소', 값: merged.상세주소 || '' },
        { 컬럼명: '@성별코드', 값: merged.성별코드 || '031001' },
        { 컬럼명: '@변경자', 값: String(userNo) },
        { 컬럼명: '@프로필', 값: merged.프로필 || '' },
        { 컬럼명: '@역할목록', 값: merged.역할목록 || '[]' },
      ];

      const payload = { Table1: table1 };

      const url = `${API_URL}?procedureName=DBO.TRP_T_%EC%82%AC%EC%9B%90%EC%88%98%EC%A0%95_%EA%B0%9C%EB%B3%84`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setProfileUploadError('프로필 정보 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      await response.json().catch(() => null);

      setOriginalStaffInfo((prev) => ({
        ...(prev || {}),
        프로필: profilePathForDb,
      }));
      setUserInfo((prev) => ({
        ...(prev || {}),
        profile: profileUrl,
      }));
    } catch (error) {
      console.error('Failed to upload profile image:', error);
      setProfileUploadError('프로필 이미지 업로드 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsUploadingProfile(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleOpenCompanyInfo = async () => {
    if (!isAdminUser) {
      return;
    }
    setIsCompanyModalOpen(true);
    setCompanyModalTab('company');
    setCompanyStaffList(null);
    setCompanyStaffListError('');
    setShowResignedStaff(false);
    setSelectedCompanyStaffRow(null);
    setSelectedCompanyStaffDetail(null);
    setCompanyStaffSaveError('');
    setCompanyStaffEditorMode(null);
    setIsLoadingCompanyInfo(true);
    setLogoUploadError('');
    setCompanySaveError('');
    await fetchCompanyInfo();
  };

  const handleSaveCompanyInfo = async () => {
    if (!companyInfo || !originalCompanyInfo) {
      return;
    }

    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        setCompanySaveError('로그인 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.');
        return;
      }

      const mergedInfo = {
        companyName: (companyInfo.companyName != null && companyInfo.companyName !== '')
          ? companyInfo.companyName
          : (originalCompanyInfo.companyName || ''),
        ceoName: (companyInfo.ceoName != null && companyInfo.ceoName !== '')
          ? companyInfo.ceoName
          : (originalCompanyInfo.ceoName || ''),
        phone: (companyInfo.phone != null && companyInfo.phone !== '')
          ? companyInfo.phone
          : (originalCompanyInfo.phone || ''),
        email: (companyInfo.email != null && companyInfo.email !== '')
          ? companyInfo.email
          : (originalCompanyInfo.email || ''),
        zipCode: (companyInfo.zipCode != null && companyInfo.zipCode !== '')
          ? companyInfo.zipCode
          : (originalCompanyInfo.zipCode || ''),
        address1: (companyInfo.address1 != null && companyInfo.address1 !== '')
          ? companyInfo.address1
          : (originalCompanyInfo.address1 || ''),
        address2: (companyInfo.address2 != null && companyInfo.address2 !== '')
          ? companyInfo.address2
          : (originalCompanyInfo.address2 || ''),
      };

      const isChanged =
        mergedInfo.companyName !== (originalCompanyInfo.companyName || '') ||
        mergedInfo.ceoName !== (originalCompanyInfo.ceoName || '') ||
        mergedInfo.phone !== (originalCompanyInfo.phone || '') ||
        mergedInfo.email !== (originalCompanyInfo.email || '') ||
        mergedInfo.zipCode !== (originalCompanyInfo.zipCode || '') ||
        mergedInfo.address1 !== (originalCompanyInfo.address1 || '') ||
        mergedInfo.address2 !== (originalCompanyInfo.address2 || '');

      if (!isChanged) {
        return;
      }

      const table1 = [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@업체명', 값: mergedInfo.companyName || '', 리스트값: null },
        { 컬럼명: '@대표자', 값: mergedInfo.ceoName || '', 리스트값: null },
        { 컬럼명: '@대표전화', 값: mergedInfo.phone || '', 리스트값: null },
        { 컬럼명: '@대표이메일', 값: mergedInfo.email || '', 리스트값: null },
        { 컬럼명: '@우편번호', 값: mergedInfo.zipCode || '', 리스트값: null },
        { 컬럼명: '@기본주소', 값: mergedInfo.address1 || '', 리스트값: null },
        { 컬럼명: '@상세주소', 값: mergedInfo.address2 || '', 리스트값: null },
        { 컬럼명: '@변경자', 값: String(userNo), 리스트값: null },
      ];

      const payload = { Table1: table1 };

      setIsSavingCompany(true);
      setCompanySaveError('');

      const url = `${API_URL}?procedureName=DBO.RCT_T_%ED%9A%8C%EC%82%AC%EC%A0%95%EB%B3%B4_%EA%B8%B0%EB%B3%B8%EC%A0%95%EB%B3%B4`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setCompanySaveError('회사정보 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      try {
        // 정상 응답은 보통 {} 형태
        await response.json().catch(() => null);
      } catch (_) {
        // 응답 파싱 실패는 치명적이지 않으므로 무시
      }

      setOriginalCompanyInfo(mergedInfo);

      try {
        window.alert('회사정보가 정상적으로 저장되었습니다.');
      } catch (_) {
        // window가 없는 환경은 무시
      }
    } catch (error) {
      console.error('Failed to save company info:', error);
      setCompanySaveError('회사정보 저장 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsSavingCompany(false);
    }
  };

  // Function to get current menu name based on path
  const getCurrentMenuName = () => {
    const path = location.pathname;
    switch (path) {
      case '/':
        return '홈 > 상품 관리 - 투어';
      case '/products':
        return '상품 관리 - 투어';
      case '/quotes':
        return '상담 관리';
      case '/reservations':
        return '예약 관리';
      default:
        if (path.startsWith('/products/tour')) return '상품 관리 - 투어';
        if (path.startsWith('/products/quote')) return '상품 관리 - 견적상품';
        if (path.startsWith('/products/hotel')) return '상품 관리 - 호텔';
        if (path.startsWith('/products/single')) return '상품 관리 - 단품';
        if (path.startsWith('/quick-wizard')) return '스마트 상품 등록';
        if (path.startsWith('/general/price-stock')) return '일반상품 가격/재고 관리';
        if (path.startsWith('/general/detail-settings')) return '상세 페이지 설정';
        if (path.startsWith('/detail-settings')) return '상세 페이지 설정';
        if (path.startsWith('/price-stock')) return '가격/재고 관리';
        if (path.startsWith('/reservation-detail')) return '예약 관리';
        if (path.startsWith('/schedule')) return '일정관리';
        if (path.startsWith('/settings/group-settlement')) return '단체정산';
        if (path.startsWith('/quotes/open')) return '상담 관리 - 공개 견적 요청(개발중)';
        if (path.startsWith('/quote-detail')) return '상담 상세';
        if (path.startsWith('/orders')) return '주문 내역';
        if (path.startsWith('/settings')) return '설정';
        if (path.startsWith('/settlements')) return '예약 관리';
        if (path.startsWith('/analytics/clarity')) return '방문 통계';
        if (path.startsWith('/manuals')) return '메뉴얼';
        return '';
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const resetPasswordForm = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordChangeMessage('');
  };

  const handleOpenPasswordModal = () => {
    resetPasswordForm();
    setIsPasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setIsPasswordModalOpen(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordChangeMessage('');

    if (!oldPassword || !newPassword || !confirmNewPassword) {
      setPasswordChangeMessage('모든 비밀번호 필드를 입력해 주세요.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordChangeMessage('변경할 비밀번호가 서로 일치하지 않습니다.');
      return;
    }

    const { userNo } = getUserInfo() || {};
    if (!userNo) {
      setPasswordChangeMessage('로그인 사용자 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.');
      return;
    }

    try {
      setIsChangingPassword(true);
      const url = `https://trpliveapi1.tripbox.co.kr/ERP/ChangePassword?userNo=${encodeURIComponent(
        userNo
      )}&oldPassword=${encodeURIComponent(oldPassword)}&userPassword=${encodeURIComponent(newPassword)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...getAuthHeader(),
        },
      });

      const text = await response.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch (_) {
        // not JSON, keep as plain text
      }

      const isSuccess =
        text.trim().toLowerCase() === 'success' ||
        text.includes('success') ||
        (data && (data.result === 'success' || data.status === 'success'));

      if (isSuccess) {
        resetPasswordForm();
        setIsPasswordModalOpen(false);
        try {
          window.alert('비밀번호가 정상적으로 변경되었습니다.');
        } catch (_) {
          // window가 없는 환경은 무시
        }
        return;
      }

      const errorMessage = data && data.message ? data.message : '비밀번호 변경에 실패했습니다. 다시 시도해 주세요.';

      if (errorMessage.includes('이전 비밀번호가 잘못되었습니다')) {
        setPasswordChangeMessage('비밀번호 변경 실패: 이전 비밀번호가 잘못되었습니다.');
      } else {
        setPasswordChangeMessage(errorMessage);
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordChangeMessage('비밀번호 변경 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const onLogout = () => {
    clearAuth();
    // 데모 체험 중 로그아웃한 경우, 데모 표시를 남겨 두면 다음 실제 로그인까지
    // API 인터셉터가 붙어 있게 되므로 함께 정리한다.
    endDemoSession();
    navigate('/login', { replace: true });
  };

  const getProfileImageUrl = () => {
    if (!userInfo || !userInfo.profile) return null;
    const value = userInfo.profile;
    if (typeof value !== 'string') return null;

    // Build full URL first (absolute URL stays as-is, otherwise prefix with object storage domain)
    const fullUrl = /^https?:\/\//i.test(value)
      ? value
      : `https://kr.object.iwinv.kr/orgin/${value}`;

    // If the last path segment has no file extension, treat it as invalid and fall back to default profile image
    try {
      const fileName = fullUrl.split('?')[0].split('#')[0].split('/').pop() || '';
      if (!fileName || !fileName.includes('.')) {
        // Invalid or extension-less file name: use default profile image instead
        return DEFAULT_PROFILE_IMAGE;
      }
    } catch (_) {
      // In case of any unexpected parsing issues, fall back to default profile image
      return DEFAULT_PROFILE_IMAGE;
    }

    return fullUrl;
  };

  // Product list + detail/price/orders editors: constrain height so only inner panels scroll (no layout-level scrollbar)
  const isProductManagementRoute =
    path.startsWith('/products') ||
    path.startsWith('/detail-settings') ||
    path.startsWith('/price-stock') ||
    path.startsWith('/orders') ||
    path.startsWith('/general/detail-settings') ||
    path.startsWith('/general/price-stock') ||
    path.startsWith('/general/orders');

  return (
    <div className="flex h-screen pt-16">
      {/* Sidebar - Full height */}
      {/* 라이트 모드에서는 개편 전처럼 남색 메뉴: 이 영역에만 dark 를 붙여 글자·아이콘을 밝게 뒤집고 바탕은 index.css 의 .trp-sidebar-navy */}
      <div className={`trp-sidebar ${colorMode === 'light' ? 'dark trp-sidebar-navy' : ''} ${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 ease-in-out relative group flex flex-col overflow-visible`}>
        {/* Sidebar content wrapper */}
        <div className="flex flex-col h-full">

          <nav className="relative mt-4 px-2 flex-1 overflow-y-auto overflow-x-visible">
            {(() => {
              const path = location.pathname;
              const isHome = path === '/settlements';
              const isProductsRoute = path.startsWith('/products') || path.startsWith('/detail-settings') || path.startsWith('/price-stock') || path.startsWith('/quick-wizard') || path.startsWith('/orders');
              const isAnalytics = path.startsWith('/analytics/clarity');
              const isGroupSettlement = path.startsWith('/settings/group-settlement');
              const isSettingsRoute = (path.startsWith('/settings') && !isGroupSettlement) || isAnalytics;
              const isProducts = isProductsRoute || (!sidebarOpen && activeFlyout === 'products');
              const isSettings = isSettingsRoute;
              const isQuotes = path === '/quotes' || path.startsWith('/quotes/open') || path.startsWith('/quote-detail');
              const isSchedule = path === '/schedule';
              const baseClasses = 'flex items-center px-3 py-2 rounded-lg text-[14px] font-semibold transition-colors';
              const inactive = 'text-gray-900 hover:bg-gray-100';
              const active = 'bg-blue-600 text-white';
              return (
                <>
                  {/* Products Group - Accordion / Flyout when collapsed */}
                  <div
                    ref={productsMenuRef}
                    className={`mt-2 rounded-lg  group relative`}
                    onMouseEnter={() => {
                      if (!sidebarOpen && productsMenuRef.current) {
                        clearFlyoutTimer();
                        const rect = productsMenuRef.current.getBoundingClientRect();
                        setFlyoutPosition({ top: rect.top, left: rect.right + 8 });
                        setActiveFlyout('products');
                      }
                    }}
                    onMouseLeave={() => {
                      if (!sidebarOpen) {
                        scheduleFlyoutClose();
                      }
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (!sidebarOpen) {
                          // In collapsed mode, show the flyout and navigate to the first child menu
                          setActiveFlyout('products');
                          navigate('/products/tour');
                          return;
                        }
                        // In expanded mode, open the accordion and navigate to the first child menu
                        setProductsOpen(true);
                        navigate('/products/tour');
                      }}
                      className={`${baseClasses} w-full ${!sidebarOpen ? 'flex-col justify-center py-3 text-center' : 'text-left'} ${isProducts ? 'bg-blue-50 text-blue-700' : 'text-gray-900 hover:bg-gray-100'}`}
                      aria-expanded={productsOpen}
                      aria-controls="products-accordion"
                    >
                      {isProducts ? (
                        <MapPinIconSolid className="w-5 h-5 text-blue-600" />
                      ) : (
                        <MapPinIconOutline className="w-5 h-5 text-gray-500 group-hover:text-gray-900 transition-colors" />
                      )}
                      {sidebarOpen && <span className="ml-3 flex-1 whitespace-nowrap">상품 관리</span>}
                      {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">상품 관리</span>}
                      {sidebarOpen && (
                        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    {sidebarOpen && (
                      <div
                        id="products-accordion"
                        className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${productsOpen ? 'max-h-60' : 'max-h-0'}`}
                      >
                        <div className="ml-11 mt-1 mb-2 space-y-1 pr-2">
                          <Link to="/products/tour" className={`block text-sm px-3 py-2 rounded-md transition-all duration-200 ${path.startsWith('/products/tour') ? 'text-blue-700 font-bold bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
                            <span className="inline-flex items-center space-x-2">
                              <MapIcon className={`w-4 h-4 ${path.startsWith('/products/tour') ? 'text-blue-600' : 'text-gray-400'}`} />
                              <span>투어</span>
                            </span>
                          </Link>
                          <Link to="/products/hotel" className={`block text-sm px-3 py-2 rounded-md transition-all duration-200 ${path.startsWith('/products/hotel') ? 'text-blue-700 font-bold bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
                            <span className="inline-flex items-center space-x-2">
                              <BuildingOfficeIcon className={`w-4 h-4 ${path.startsWith('/products/hotel') ? 'text-blue-600' : 'text-gray-400'}`} />
                              <span>호텔</span>
                            </span>
                          </Link>
                          <Link to="/products/single" className={`block text-sm px-3 py-2 rounded-md transition-all duration-200 ${path.startsWith('/products/single') ? 'text-blue-700 font-bold bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
                            <span className="inline-flex items-center space-x-2">
                              <CubeIcon className={`w-4 h-4 ${path.startsWith('/products/single') ? 'text-blue-600' : 'text-gray-400'}`} />
                              <span>일반상품</span>
                            </span>
                          </Link>
                          <Link to="/products/quote" className={`block text-sm px-3 py-2 rounded-md transition-all duration-200 ${path.startsWith('/products/quote') ? 'text-blue-700 font-bold bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
                            <span className="inline-flex items-center space-x-2">
                              <DocumentTextIconOutline className={`w-4 h-4 ${path.startsWith('/products/quote') ? 'text-blue-600' : 'text-gray-400'}`} />
                              <span>견적상품</span>
                            </span>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                  {sidebarOpen && <div className="my-3 border-t border-gray-200" />}
                  <Link to="/quotes" className={`${baseClasses} ${!sidebarOpen ? 'flex-col justify-center py-3' : ''} mt-2 ${isQuotes ? active : inactive} group`} aria-current={isQuotes ? 'page' : undefined}>
                    {isQuotes ? (
                      <DocumentTextIconSolid className="w-5 h-5 text-white" />
                    ) : (
                      <DocumentTextIconOutline className="w-5 h-5 text-gray-500 group-hover:text-gray-900 transition-colors" />
                    )}
                    {sidebarOpen && <span className="ml-3 whitespace-nowrap">상담 관리</span>}
                    {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">상담 관리</span>}
                  </Link>
                  <Link to="/settlements" className={`${baseClasses} ${!sidebarOpen ? 'flex-col justify-center py-3' : ''} mt-2 ${isHome ? active : inactive} group`} aria-current={isHome ? 'page' : undefined}>
                    {isHome ? (
                      <PerformanceIconSolid className="w-5 h-5 text-white" />
                    ) : (
                      <PerformanceIconOutline className="w-5 h-5 text-gray-500 group-hover:text-gray-900 transition-colors" />
                    )}
                    {sidebarOpen && <span className="ml-3 whitespace-nowrap">예약 관리</span>}
                    {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">예약 관리</span>}
                  </Link>
                  <Link to="/schedule" className={`${baseClasses} ${!sidebarOpen ? 'flex-col justify-center py-3' : ''} mt-2 ${isSchedule ? active : inactive} group`} aria-current={isSchedule ? 'page' : undefined}>
                    {isSchedule ? (
                      <CalendarIconSolid className="w-5 h-5 text-white" />
                    ) : (
                      <CalendarIconOutline className="w-5 h-5 text-gray-500 group-hover:text-gray-900 transition-colors" />
                    )}
                    {sidebarOpen && <span className="ml-3 whitespace-nowrap">일정관리</span>}
                    {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">일정관리</span>}
                  </Link>
                  <Link to="/settings/group-settlement" className={`${baseClasses} ${!sidebarOpen ? 'flex-col justify-center py-3' : ''} mt-2 ${isGroupSettlement ? active : inactive} group`} aria-current={isGroupSettlement ? 'page' : undefined}>
                    {isGroupSettlement ? (
                      <BanknotesIconSolid className="w-5 h-5 text-white" />
                    ) : (
                      <BanknotesIconOutline className="w-5 h-5 text-gray-500 group-hover:text-gray-900 transition-colors" />
                    )}
                    {sidebarOpen && <span className="ml-3 whitespace-nowrap">단체정산</span>}
                    {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">단체정산</span>}
                  </Link>
                </>
              );
            })()}
          </nav>
          <div className="border-t border-gray-200 px-2 py-2 bg-white">
            <button
              type="button"
              onClick={toggleColorMode}
              title={colorMode === 'dark' ? '밝은 화면으로' : '어두운 화면으로'}
              aria-label={colorMode === 'dark' ? '밝은 화면으로' : '어두운 화면으로'}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-[13px] font-semibold text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 ${!sidebarOpen ? 'flex-col justify-center' : ''}`}
            >
              {colorMode === 'dark' ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
              {sidebarOpen && <span className="ml-3">{colorMode === 'dark' ? '밝은 화면' : '어두운 화면'}</span>}
              {!sidebarOpen && <span className="mt-1 text-[10px] leading-none">{colorMode === 'dark' ? '밝게' : '어둡게'}</span>}
            </button>
            <ExchangeRateWidget compact={!sidebarOpen} />
            {isAdminUser && (() => {
              const path = location.pathname;
              const isAnalytics = path.startsWith('/analytics/clarity');
              const isGroupSettlement = path.startsWith('/settings/group-settlement');
              const isSettings = (path.startsWith('/settings') && !isGroupSettlement) || isAnalytics;
              const baseClasses = 'flex items-center px-3 py-2 rounded-lg text-[14px] font-semibold transition-colors';
              const inactive = 'text-gray-900 hover:bg-gray-100';
              const active = 'bg-blue-600 text-white';

              return (
                <button
                  type="button"
                  onClick={() => {
                    setIsSettingsLauncherOpen(true);
                    setSettingsLauncherKeyword('');
                  }}
                  className={`${baseClasses} w-full ${!sidebarOpen ? 'flex-col justify-center py-3 text-center' : 'text-left'} ${isSettings ? active : inactive}`}
                >
                  {isSettings ? (
                    <Cog6ToothIconSolid className="w-5 h-5 text-white" />
                  ) : (
                    <Cog6ToothIconOutline className="w-5 h-5 text-gray-500 group-hover:text-gray-900 transition-colors" />
                  )}
                  {sidebarOpen && <span className="ml-3 flex-1 whitespace-nowrap">관리</span>}
                  {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">관리</span>}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
      {/* Flyout Menus - Rendered outside sidebar to avoid overflow issues */}
      {!sidebarOpen && activeFlyout === 'products' && (
          <div
            style={{ top: `${flyoutPosition.top}px`, left: `${flyoutPosition.left}px` }}
            className="fixed w-56 bg-white border border-gray-200 rounded-xl shadow-lg p-1.5 z-[60] transition-all duration-200 opacity-100"
            onMouseEnter={clearFlyoutTimer}
            onMouseLeave={scheduleFlyoutClose}
          >
            <Link to="/products/tour" onClick={() => { setActiveFlyout(null); }} className={`block text-sm px-3 py-2 rounded-lg transition-colors ${path.startsWith('/products/tour') ? 'text-blue-700 font-bold bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
              <span className="inline-flex items-center space-x-2">
                <MapIcon className={`w-4 h-4 ${path.startsWith('/products/tour') ? 'text-blue-600' : 'text-gray-400'}`} />
                <span>투어</span>
              </span>
            </Link>
            <Link to="/products/hotel" onClick={() => { setActiveFlyout(null); }} className={`block text-sm px-3 py-2 rounded-lg transition-colors ${path.startsWith('/products/hotel') ? 'text-blue-700 font-bold bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
              <span className="inline-flex items-center space-x-2">
                <BuildingOfficeIcon className={`w-4 h-4 ${path.startsWith('/products/hotel') ? 'text-blue-600' : 'text-gray-400'}`} />
                <span>호텔</span>
              </span>
            </Link>
            <Link to="/products/single" onClick={() => { setActiveFlyout(null); }} className={`block text-sm px-3 py-2 rounded-lg transition-colors ${path.startsWith('/products/single') ? 'text-blue-700 font-bold bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
              <span className="inline-flex items-center space-x-2">
                <CubeIcon className={`w-4 h-4 ${path.startsWith('/products/single') ? 'text-blue-600' : 'text-gray-400'}`} />
                <span>일반상품</span>
              </span>
            </Link>
            <Link to="/products/quote" onClick={() => { setActiveFlyout(null); }} className={`block text-sm px-3 py-2 rounded-lg transition-colors ${path.startsWith('/products/quote') ? 'text-blue-700 font-bold bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
              <span className="inline-flex items-center space-x-2">
                <DocumentTextIconOutline className={`w-4 h-4 ${path.startsWith('/products/quote') ? 'text-blue-600' : 'text-gray-400'}`} />
                <span>견적상품</span>
              </span>
            </Link>
          </div>
        )}
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 fixed top-0 left-0 w-screen z-50">
          {ENABLE_HEADER_CHERRY_BLOSSOM && (
            <div className="header-cherry-blossom-container">
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
              <div className="header-cherry-blossom" />
            </div>
          )}
          <div className="grid grid-cols-[240px_1fr_auto] items-center h-16 pl-4 pr-12 gap-4">
            <div className="w-96 flex items-center min-w-[240px] space-x-3">
              {userProfile.companyLogo ? (
                <div className="h-8 w-auto flex items-center">
                  <img
                    src={userProfile.companyLogo}
                    alt="Company Logo"
                    className="h-8 w-auto object-contain"
                  />
                </div>
              ) : userProfile.companyName ? (
                <div className="text-sm font-semibold text-gray-900 leading-tight truncate">
                  {userProfile.companyName}
                </div>
              ) : null}
              <h2 className="min-w-0 truncate border-l border-gray-200 pl-3 text-[16px] font-bold tracking-tight text-gray-900">{getCurrentMenuName()}</h2>
            </div>
            <div className="flex items-center justify-center overflow-hidden">
              {rssItems && rssItems.length > 0 && rssItems[currentRssIndex] && (
                <div className="max-w-3xl w-full overflow-hidden">
                  <div className="flex items-center space-x-3 text-[11px] text-gray-600 whitespace-nowrap">
                    <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 font-semibold flex-shrink-0">여행신문</span>
                    <a
                      href={rssItems[currentRssIndex].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-blue-600 transition-colors truncate flex-1"
                    >
                      {rssItems[currentRssIndex].title}
                    </a>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3 min-w-[260px] justify-end">
              <div className="relative w-72 md:w-[280px]" ref={globalSearchRef}>
                <div className="flex items-center space-x-2">
                  <select
                    className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={globalSearchDomain}
                    onChange={(e) => {
                      setGlobalSearchDomain(e.target.value);
                      setGlobalSearchResults([]);
                    }}
                  >
                    <option value="product">상품</option>
                    <option value="reservation">예약</option>
                    <option value="consult">상담</option>
                  </select>
                  <input
                    type="text"
                    className="flex-1 px-2.5 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={globalSearchKeyword}
                    onChange={(e) => setGlobalSearchKeyword(e.target.value)}
                    placeholder={
                      globalSearchDomain === 'product'
                        ? '상품번호 / 상품명 / 도시 / 국가 / 상품담당으로 검색'
                        : globalSearchDomain === 'reservation'
                          ? '판매상품명 / 이름 / 휴대폰 / 이메일 / 대리점 / 주문자 검색'
                          : '견적명 / 요청자명 / 휴대폰 / 이메일 / 견적내용 검색'
                    }
                  />
                  {isGlobalSearchLoading && (
                    <span className="text-[11px] text-gray-500 whitespace-nowrap">검색 중...</span>
                  )}
                </div>

                {globalSearchKeyword && (globalSearchResults.length > 0 || globalSearchError) && (
                  <div className="absolute top-full left-1/2 mt-1 w-[600px] -translate-x-1/2">
                    <div className="bg-white border border-gray-200 rounded-md shadow-lg text-xs text-gray-800 max-h-96 overflow-auto">
                      {globalSearchError && (
                        <div className="px-3 py-2 text-red-600 border-b border-gray-100">{globalSearchError}</div>
                      )}
                      {!globalSearchError && globalSearchResults.length === 0 && (
                        <div className="px-3 py-2 text-gray-500">검색 결과가 없습니다.</div>
                      )}
                      {globalSearchResults.map((item, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSelectGlobalSearchResult(item)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-50 last:border-b-0 flex flex-col"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-semibold truncate mr-2">{item.label}</span>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-gray-200 text-[10px] text-gray-500">
                              {item.type === 'product' ? '상품' : item.type === 'reservation' ? '예약' : '상담'}
                            </span>
                          </div>
                          {item.subLabel && (
                            <div className="mt-0.5 text-[11px] text-gray-500 truncate">{item.subLabel}</div>
                          )}
                          {item.extra && (
                            <div className="mt-0.5 text-[11px] text-gray-400 truncate">담당: {item.extra}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative">
                {/* Tip entry for Smart Guide */}
                <button
                  type="button"
                  onClick={() => openSmartGuide('price', { fromTip: true })}
                  className="inline-flex items-center px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                  aria-label="Tip"
                  title="Tip"
                >
                  <LightBulbIcon className="w-5 h-5 text-amber-500" />
                </button>
              </div>
              <div className="relative" ref={profileMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                  className="flex items-center justify-center px-1.5 py-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  aria-haspopup="true"
                  aria-expanded={isProfileMenuOpen}
                >
                  <div className="flex flex-col items-center mr-1.5">
                    {getProfileImageUrl() ? (
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200">
                        <img
                          src={getProfileImageUrl()}
                          alt="프로필 이미지"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = DEFAULT_PROFILE_IMAGE;
                          }}
                        />
                      </div>
                    ) : (
                      <UserCircleIcon className="w-10 h-10 text-gray-500" />
                    )}
                    {(userProfile.userName || userProfile.companyName) && (
                      <div className="mt-0.5 text-[11px] font-medium text-gray-800 max-w-[120px] truncate">
                        {userProfile.userName || userProfile.companyName}
                      </div>
                    )}
                  </div>
                  <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                </button>
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 py-2 z-50">
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        handleOpenPasswordModal();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-2">
                        <KeyIcon className="w-4 h-4 text-gray-500" />
                        <span>비밀번호 변경</span>
                      </div>
                    </button>
                    {isAdminUser && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          handleOpenCompanyInfo();
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-2">
                          <BuildingOfficeIcon className="w-4 h-4 text-gray-500" />
                          <span>회사정보</span>
                        </div>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        onLogout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-2">
                        <ArrowRightOnRectangleIcon className="w-4 h-4 text-red-500" />
                        <span>로그아웃</span>
                      </div>
                    </button>
                    {(userProfile.companyName || userProfile.userName) && (
                      <>
                        <div className="my-1 border-t border-gray-100" />
                        <div className="px-4 py-2 text-xs text-gray-500">
                          {userProfile.companyName && (
                            <div className="font-medium text-gray-700">{userProfile.companyName}</div>
                          )}
                          {userProfile.userName && (
                            <button
                              type="button"
                              className="mt-0.5 text-left w-full text-gray-600 hover:text-blue-600"
                              onClick={() => {
                                setIsProfileMenuOpen(false);
                                fetchUserProfileAndStaffInfo();
                                setIsEditingProfile(true);
                                setIsUserInfoModalOpen(true);
                              }}
                            >
                              <span className="inline-flex items-center space-x-1">
                                <UserIcon className="w-3.5 h-3.5 text-gray-500" />
                                <span>{userProfile.userName}</span>
                              </span>
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {isAdminUser && isSettingsLauncherOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setIsSettingsLauncherOpen(false)}
            />
            <div className="relative z-10 w-[min(960px,96vw)] max-h-[85vh] overflow-auto bg-white rounded-2xl shadow-2xl border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="text-base font-semibold text-gray-900">관리</div>
                <button
                  type="button"
                  onClick={() => setIsSettingsLauncherOpen(false)}
                  className="inline-flex items-center justify-center rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  aria-label="닫기"
                >
                  <span className="text-base leading-none">×</span>
                </button>
              </div>
              <div className="px-5 py-4">
                
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[
                    { key: 'clients', label: '거래처 관리', icon: <UserIcon className="w-5 h-5" />, onClick: () => navigate('/settings/clients') },
                    { key: 'partners', label: '공급사 관리', icon: <BuildingOfficeIcon className="w-5 h-5" />, onClick: () => navigate('/settings/partners') },
                    { key: 'accounts', label: '통장관리', icon: <BanknotesIconOutline className="w-5 h-5" />, onClick: () => navigate('/settings/accounts') },
                    { key: 'pg', label: 'PG거래내역 조회', icon: <CreditCardIcon className="w-5 h-5" />, onClick: () => navigate('/settings/pg-transactions') },
                    { key: 'shared-inventory', label: '공유재고 관리', icon: <Squares2X2Icon className="w-5 h-5" />, onClick: () => navigate('/settings/shared-inventory') },
                    { key: 'product-codes', label: '상품코드 관리', icon: <TagIcon className="w-5 h-5" />, onClick: () => navigate('/settings/product-codes') },
                    { key: 'promotions', label: '기획전 관리', icon: <TicketIcon className="w-5 h-5" />, onClick: () => navigate('/settings/promotions') },
                    { key: 'outgoing-mail', label: '발신메일 관리', icon: <PaperAirplaneIcon className="w-5 h-5" />, onClick: () => navigate('/settings/outgoing-mail') },
                    { key: 'group-settlement', label: '단체정산', icon: <PerformanceIconOutline className="w-5 h-5" />, onClick: () => navigate('/settings/group-settlement') },
                    { key: 'clarity', label: '방문 통계', icon: <ChartPieIconOutline className="w-5 h-5" />, onClick: () => navigate('/analytics/clarity') },
                    { key: 'manuals', label: '메뉴얼', icon: <BookOpenIcon className="w-5 h-5" />, onClick: () => navigate('/manuals') },
                  ]
                    .filter((it) => {
                      const kw = (settingsLauncherKeyword || '').trim().toLowerCase();
                      if (!kw) return true;
                      return String(it.label || '').toLowerCase().includes(kw);
                    })
                    .map((it) => (
                      <button
                        key={it.key}
                        type="button"
                        onClick={() => {
                          setIsSettingsLauncherOpen(false);
                          it.onClick();
                        }}
                        className="relative w-full text-left rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors px-4 py-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="text-gray-600">{it.icon}</div>
                            <div className="font-medium text-gray-900 text-sm">{it.label}</div>
                          </div>
                          {it.hasDot && (
                            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-red-500" />
                          )}
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Main Content */}
        <div className="flex min-h-0 flex-1 flex-col">
          {workTabs.length > 0 && (
            <div className="relative z-10 mt-2 px-3 mx-0 md:mx-10">
              <div className="flex items-end gap-0.5 overflow-x-auto">
                {workTabs.map((tab) => {
                  if (tab.kind === 'group') {
                    const isActive = tab.entries.some((e) => e.id === activeWorkTabId);
                    const menuOpen = openWorkTabGroupKey === tab.groupKey;
                    const count = tab.entries.length;
                    return (
                      <div
                        key={tab.groupKey}
                        ref={(el) => {
                          workTabGroupWrapRefs.current[tab.groupKey] = el;
                        }}
                        data-work-tab-group-wrap
                        className={`relative inline-flex h-9 shrink-0 items-stretch rounded-t-xl font-semibold transition-colors ${
                          isActive
                            ? 'bg-white text-gray-900 shadow-[0_-2px_8px_rgb(0_0_0/0.06)]'
                            : 'bg-gray-200 text-gray-500 hover:bg-gray-300 hover:text-gray-900'
                        }`}
                        onMouseEnter={() => {
                          clearWorkTabHoverCloseTimer();
                          if (tab.entries.length > 1) setOpenWorkTabGroupKey(tab.groupKey);
                        }}
                        onMouseLeave={scheduleWorkTabHoverClose}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (tab.entries.length === 1) {
                              handleOpenWorkTabEntry(tab.entries[0]);
                              return;
                            }
                            const current = tab.entries.find((e) => e.id === activeWorkTabId);
                            handleOpenWorkTabEntry(current || tab.entries[0]);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 text-sm whitespace-nowrap transition-colors"
                          title={getWorkTabHoverTitle(tab, activeWorkTabId)}
                        >
                          <span>{tab.label}</span>
                          <span
                            className={`inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 py-px text-xs font-semibold tabular-nums ${
                              isActive
                                ? 'bg-gray-200 text-gray-800'
                                : 'bg-gray-300/80 text-gray-700'
                            }`}
                          >
                            {count}
                          </span>
                          {tab.entries.length > 1 && (
                            <ChevronDownIcon
                              className={`h-3.5 w-3.5 shrink-0 opacity-70 ${menuOpen ? 'rotate-180' : ''}`}
                            />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseWorkTab(tab, 'group');
                          }}
                          className={`mr-2 inline-flex items-center self-center transition-colors ${
                            isActive
                              ? 'h-4 w-4 justify-center rounded-full bg-gray-400 text-white hover:bg-gray-500'
                              : 'rounded p-0.5 text-gray-400 hover:text-gray-600'
                          }`}
                          aria-label={`${tab.label} 그룹에서 현재 화면 닫기`}
                        >
                          <XMarkIcon className={isActive ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} />
                        </button>
                      </div>
                    );
                  }

                  const isActive = tab.id === activeWorkTabId;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => handleOpenWorkTab(tab)}
                      className={`group inline-flex h-9 shrink-0 items-center gap-1.5 rounded-t-xl px-3 text-sm font-semibold whitespace-nowrap transition-colors ${
                        isActive
                          ? 'bg-white text-gray-900 shadow-[0_-2px_8px_rgb(0_0_0/0.06)]'
                          : 'bg-gray-200 text-gray-500 hover:bg-gray-300 hover:text-gray-900'
                      }`}
                      title={getWorkTabHoverTitle(tab, activeWorkTabId)}
                    >
                      <span>{tab.label}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseWorkTab(tab, 'simple');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCloseWorkTab(tab, 'simple');
                          }
                        }}
                        className={`inline-flex items-center justify-center transition-colors ${
                          isActive
                            ? 'h-4 w-4 rounded-full bg-gray-400 text-white hover:bg-gray-500'
                            : 'rounded p-0.5 text-gray-400 hover:text-gray-600'
                        }`}
                        aria-label={`${tab.label} 탭 닫기`}
                      >
                        <XMarkIcon className={isActive ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {openWorkTabGroupKey &&
            workTabDropdownPos &&
            typeof document !== 'undefined' &&
            (() => {
              const g = workTabs.find(
                (t) =>
                  t.kind === 'group' &&
                  t.groupKey === openWorkTabGroupKey &&
                  (t.entries?.length || 0) > 1
              );
              if (!g) return null;
              return createPortal(
                <div
                  data-work-tab-group-portal
                  className="fixed overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-xl"
                  style={{
                    top: workTabDropdownPos.top,
                    left: workTabDropdownPos.left,
                    minWidth: workTabDropdownPos.minWidth,
                    zIndex: 10050,
                  }}
                  role="presentation"
                  onMouseEnter={clearWorkTabHoverCloseTimer}
                  onMouseLeave={scheduleWorkTabHoverClose}
                >
                  <ul
                    className="max-h-64 min-w-full overflow-auto"
                    role="listbox"
                  >
                    {g.entries.map((entry) => (
                      <li key={entry.id} role="none" className="flex items-stretch">
                        <button
                          type="button"
                          role="option"
                          aria-selected={entry.id === activeWorkTabId}
                          className={`flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-sm ${
                            entry.id === activeWorkTabId
                              ? 'bg-blue-50 text-blue-800'
                              : 'text-slate-800 hover:bg-slate-50'
                          }`}
                          onClick={() => handleOpenWorkTabEntry(entry)}
                        >
                          <span className="truncate">{getWorkTabEntryDropdownLabel(entry)}</span>
                        </button>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center border-l border-slate-100 px-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                          aria-label={`${getWorkTabEntryDropdownLabel(entry)} 작업 목록에서 제거`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveWorkTabGroupEntry(g.groupKey, entry.id);
                          }}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>,
                document.body
              );
            })()}
          <main className={`mt-3 flex-1 min-h-0 ${isProductManagementRoute ? 'overflow-hidden' : 'overflow-auto'}`}>
            <div className={`px-3 mx-0 md:mx-10 ${isProductManagementRoute ? 'h-full min-h-0 flex flex-col' : ''}`}>
              <Outlet />
            </div>

            {isAdminUser && isCompanyModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-lg shadow-lg w-[95vw] max-w-6xl p-5">
                  <h3 className="text-base font-semibold text-gray-800">회사정보</h3>

                  <div className="mt-3 flex items-center space-x-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() => setCompanyModalTab('company')}
                      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${companyModalTab === 'company' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-blue-600'}`}
                    >
                      회사정보
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCompanyModalTab('staff');
                        if (companyStaffList == null && !isLoadingCompanyStaffList) {
                          fetchCompanyStaffList();
                        }
                      }}
                      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${companyModalTab === 'staff' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-blue-600'}`}
                    >
                      사원정보
                    </button>
                  </div>

                  <div className="mt-4">
                    {companyModalTab === 'company' ? (
                      isLoadingCompanyInfo ? (
                        <div className="py-10 text-center text-sm text-gray-500">회사 정보를 불러오는 중입니다...</div>
                      ) : companyInfo ? (
                        <div className="space-y-4 text-sm">
                          <div className="flex items-center space-x-4">
                            <div className="relative">
                              {companyInfo.companyLogo && (
                                <div className="w-20 h-20 border border-gray-200 rounded flex items-center justify-center bg-gray-50 overflow-hidden">
                                  <img
                                    src={companyInfo.companyLogo}
                                    alt="Company Logo"
                                    className="max-w-full max-h-full object-contain"
                                  />
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  if (logoFileInputRef.current) {
                                    logoFileInputRef.current.click();
                                  }
                                }}
                                className="mt-2 inline-flex items-center px-2 py-1 rounded-md border border-gray-300 bg-white text-[11px] text-gray-700 hover:bg-gray-50 shadow-sm"
                                disabled={isUploadingLogo}
                              >
                                <PencilSquareIcon className="w-3.5 h-3.5 mr-1 text-gray-500" />
                                로고 편집
                              </button>
                              <input
                                ref={logoFileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleLogoFileChange}
                                disabled={isUploadingLogo}
                              />
                              {isUploadingLogo && (
                                <div className="mt-1 text-xs text-blue-600">로고 업로드 중...</div>
                              )}
                              {logoUploadError && (
                                <div className="mt-1 text-xs text-red-500">{logoUploadError}</div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">사업자 번호</label>
                              <input
                                type="text"
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm bg-gray-50 text-gray-600 cursor-not-allowed"
                                value={formatBusinessNo(companyInfo.businessNo)}
                                readOnly
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">업체명</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.companyName || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    companyName: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">대표자명</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.ceoName || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    ceoName: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">대표전화</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.phone || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    phone: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">대표 이메일</label>
                              <input
                                type="email"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.email || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    email: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">우편번호</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.zipCode || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    zipCode: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">기본 주소</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.address1 || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    address1: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">상세 주소</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.address2 || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    address2: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="py-10 text-center text-sm text-gray-500">회사 정보를 찾을 수 없습니다.</div>
                      )
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="inline-flex items-center space-x-2 text-sm text-gray-700 select-none">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={showResignedStaff}
                              onChange={(e) => setShowResignedStaff(e.target.checked)}
                            />
                            <span>퇴직자도 보기</span>
                          </label>
                          <button
                            type="button"
                            onClick={openCompanyStaffCreator}
                            className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                            disabled={isSavingCompanyStaff || isLoadingCompanyStaffDetail}
                          >
                            사원 추가
                          </button>
                        </div>
                        {isLoadingCompanyStaffList ? (
                          <div className="py-10 text-center text-sm text-gray-500">사원 정보를 불러오는 중입니다...</div>
                        ) : companyStaffListError ? (
                          <div className="py-4 text-sm text-red-600">{companyStaffListError}</div>
                        ) : (() => {
                          const filtered = Array.isArray(companyStaffList)
                            ? companyStaffList.filter((row) => {
                              const st = row && row.상태;
                              if (showResignedStaff) return st === '활성' || st === '탈퇴';
                              return st === '활성';
                            })
                            : [];
                          if (filtered.length === 0) {
                            return <div className="py-10 text-center text-sm text-gray-500">사원 정보가 없습니다.</div>;
                          }
                          return (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="md:col-span-2 overflow-auto border border-gray-200 rounded">
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">아이디</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">유저명</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">전화번호</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">관리자여부</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">정산관리자여부</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">편집</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filtered.map((row, idx) => {
                                    const isResigned = row && row.상태 === '탈퇴';
                                    const rowClass = isResigned
                                      ? 'bg-red-50 text-red-700'
                                      : (idx % 2 === 0 ? 'bg-white text-gray-800' : 'bg-gray-50 text-gray-800');
                                    const cellClass = isResigned ? 'text-red-700' : 'text-gray-800';
                                    const canEdit = !!(row && row.유저번호);
                                    return (
                                      <tr key={idx} className={rowClass}>
                                        <td className={`px-3 py-2 border-b border-gray-100 ${cellClass}`}>{row?.아이디 || ''}</td>
                                        <td className={`px-3 py-2 border-b border-gray-100 ${cellClass}`}>{row?.유저명 || ''}</td>
                                        <td className={`px-3 py-2 border-b border-gray-100 ${cellClass}`}>{row?.대표전화 || ''}</td>
                                        <td className="px-3 py-2 border-b border-gray-100">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${row?.관리자여부 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                                            {row?.관리자여부 ? '예' : '아니오'}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 border-b border-gray-100">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${row?.정산관리자여부 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                                            {row?.정산관리자여부 ? '예' : '아니오'}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 border-b border-gray-100">
                                          <button
                                            type="button"
                                            onClick={() => openCompanyStaffEditor(row)}
                                            className="px-2 py-1 rounded border border-gray-300 bg-white text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                            disabled={!canEdit || isSavingCompanyStaff || isLoadingCompanyStaffDetail}
                                          >
                                            편집
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            <div className="md:col-span-1 border border-gray-200 rounded p-3 bg-gray-50">
                              {companyStaffEditorMode === 'create' ? (
                                <div className="space-y-3">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <div className="text-sm font-semibold text-gray-800">사원 추가</div>
                                      <div className="text-xs text-gray-500">필수 정보만 입력해 등록합니다.</div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={closeCompanyStaffEditor}
                                      className="text-xs text-gray-500 hover:text-gray-700"
                                      disabled={isSavingCompanyStaff}
                                    >
                                      닫기
                                    </button>
                                  </div>

                                  <div className="space-y-2">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">아이디(이메일)</label>
                                      <input
                                        type="email"
                                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        value={companyStaffCreateForm.loginId}
                                        onChange={(e) => setCompanyStaffCreateForm((prev) => ({ ...(prev || {}), loginId: e.target.value }))}
                                        disabled={isSavingCompanyStaff}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">한글이름</label>
                                      <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        value={companyStaffCreateForm.nameKr}
                                        onChange={(e) => setCompanyStaffCreateForm((prev) => ({ ...(prev || {}), nameKr: e.target.value }))}
                                        disabled={isSavingCompanyStaff}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">휴대폰</label>
                                      <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        value={companyStaffCreateForm.phone}
                                        onChange={(e) => setCompanyStaffCreateForm((prev) => ({ ...(prev || {}), phone: e.target.value }))}
                                        disabled={isSavingCompanyStaff}
                                      />
                                    </div>
                                    <label className="flex items-center justify-between text-sm text-gray-700">
                                      <span>관리자여부</span>
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={!!companyStaffCreateForm.isAdmin}
                                        onChange={(e) => setCompanyStaffCreateForm((prev) => ({ ...(prev || {}), isAdmin: e.target.checked }))}
                                        disabled={isSavingCompanyStaff}
                                      />
                                    </label>
                                    <label className="flex items-center justify-between text-sm text-gray-700">
                                      <span>정산관리자여부</span>
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={!!companyStaffCreateForm.isSettlementAdmin}
                                        onChange={(e) => setCompanyStaffCreateForm((prev) => ({ ...(prev || {}), isSettlementAdmin: e.target.checked }))}
                                        disabled={isSavingCompanyStaff}
                                      />
                                    </label>
                                  </div>

                                  {companyStaffSaveError && (
                                    <div className="text-xs text-red-600">{companyStaffSaveError}</div>
                                  )}

                                  <div className="flex justify-end space-x-2 pt-2">
                                    <button
                                      type="button"
                                      onClick={closeCompanyStaffEditor}
                                      className="px-3 py-1.5 rounded border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                      disabled={isSavingCompanyStaff}
                                    >
                                      취소
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleCreateCompanyStaff}
                                      className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                                      disabled={isSavingCompanyStaff}
                                    >
                                      {isSavingCompanyStaff ? '등록 중...' : '등록'}
                                    </button>
                                  </div>
                                </div>
                              ) : companyStaffEditorMode === 'edit' ? (
                                <div className="space-y-3">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <div className="text-sm font-semibold text-gray-800">{companyStaffEditForm.nameKr || selectedCompanyStaffRow?.유저명 || ''}</div>
                                      <div className="text-xs text-gray-500">{selectedCompanyStaffRow?.아이디 || ''}</div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={closeCompanyStaffEditor}
                                      className="text-xs text-gray-500 hover:text-gray-700"
                                      disabled={isSavingCompanyStaff}
                                    >
                                      닫기
                                    </button>
                                  </div>

                                  {isLoadingCompanyStaffDetail ? (
                                    <div className="py-6 text-center text-sm text-gray-500">상세 정보를 불러오는 중...</div>
                                  ) : selectedCompanyStaffDetail ? (
                                    <>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">사원명</label>
                                        <input
                                          type="text"
                                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                          value={companyStaffEditForm.nameKr || ''}
                                          onChange={(e) => setCompanyStaffEditForm((prev) => ({ ...(prev || {}), nameKr: e.target.value }))}
                                          disabled={isSavingCompanyStaff}
                                        />
                                      </div>
                                      <label className="flex items-center justify-between text-sm text-gray-700">
                                        <span>재직여부</span>
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4"
                                          checked={!!companyStaffEditForm.isEmployed}
                                          onChange={(e) => setCompanyStaffEditForm((prev) => ({ ...(prev || {}), isEmployed: e.target.checked }))}
                                          disabled={isSavingCompanyStaff}
                                        />
                                      </label>
                                      <label className="flex items-center justify-between text-sm text-gray-700">
                                        <span>관리자여부</span>
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4"
                                          checked={!!companyStaffEditForm.isAdmin}
                                          onChange={(e) => setCompanyStaffEditForm((prev) => ({ ...(prev || {}), isAdmin: e.target.checked }))}
                                          disabled={isSavingCompanyStaff}
                                        />
                                      </label>
                                      <label className="flex items-center justify-between text-sm text-gray-700">
                                        <span>정산관리자여부</span>
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4"
                                          checked={!!companyStaffEditForm.isSettlementAdmin}
                                          onChange={(e) => setCompanyStaffEditForm((prev) => ({ ...(prev || {}), isSettlementAdmin: e.target.checked }))}
                                          disabled={isSavingCompanyStaff}
                                        />
                                      </label>
                                    </>
                                  ) : (
                                    <div className="py-6 text-center text-sm text-gray-500">상세 정보가 없습니다.</div>
                                  )}

                                  {companyStaffSaveError && (
                                    <div className="text-xs text-red-600">{companyStaffSaveError}</div>
                                  )}

                                  <div className="flex justify-end space-x-2 pt-2">
                                    <button
                                      type="button"
                                      onClick={closeCompanyStaffEditor}
                                      className="px-3 py-1.5 rounded border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                      disabled={isSavingCompanyStaff}
                                    >
                                      취소
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleSaveCompanyStaff}
                                      className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                                      disabled={isSavingCompanyStaff || isLoadingCompanyStaffDetail || !selectedCompanyStaffDetail}
                                    >
                                      {isSavingCompanyStaff ? '저장 중...' : '저장'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">왼쪽 목록에서 사원을 선택해 편집하거나, 사원 추가를 눌러 등록하세요.</div>
                              )}
                            </div>
                          </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {companyModalTab === 'company' && companySaveError && (
                    <div className="mt-3 text-xs text-red-500 text-right">{companySaveError}</div>
                  )}

                  <div className="mt-5 flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setIsCompanyModalOpen(false)}
                      className="px-3 py-1.5 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                      disabled={companyModalTab === 'company' ? isSavingCompany : false}
                    >
                      닫기
                    </button>
                    {companyModalTab === 'company' && (
                      <button
                        type="button"
                        onClick={handleSaveCompanyInfo}
                        className="px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                        disabled={isSavingCompany}
                      >
                        {isSavingCompany ? '저장 중...' : '회사정보 저장'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <SmartGuideModal
              open={isSmartGuideOpen}
              tab={smartGuideTab}
              onTabChange={setSmartGuideTab}
              dontShow={smartGuideDontShow}
              onChangeDontShow={setSmartGuideDontShow}
              onClose={handleSmartGuideClose}
              onPrevTip={handlePrevSmartGuideTip}
              onNextTip={handleNextSmartGuideTip}
              hasPrevTip={hasPrevSmartGuideTip}
              hasNextTip={hasNextSmartGuideTip}
              showDontShowOption={!smartGuideFromTip}
            />

            {isPasswordModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5">
                  <h3 className="text-base font-semibold text-gray-800 mb-4">비밀번호 변경</h3>
                  <form onSubmit={handleChangePassword} className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">기존 비밀번호</label>
                      <input
                        type="password"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">변경할 비밀번호</label>
                      <input
                        type="password"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">변경할 비밀번호 확인</label>
                      <input
                        type="password"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                      />
                    </div>

                    {passwordChangeMessage && (
                      <div className="text-xs text-red-600 whitespace-pre-line">{passwordChangeMessage}</div>
                    )}

                    <div className="flex justify-end space-x-2 pt-2">
                      <button
                        type="button"
                        onClick={handleClosePasswordModal}
                        className="px-3 py-1.5 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                        disabled={isChangingPassword}
                      >
                        닫기
                      </button>
                      <button
                        type="submit"
                        className="px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                        disabled={isChangingPassword}
                      >
                        {isChangingPassword ? '변경 중...' : '비밀번호 변경'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            {isUserInfoModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
                  <h3 className="text-[15px] font-semibold text-gray-900 mb-4">프로필 설정</h3>
                  {userInfo ? (
                    <div className="space-y-5 text-sm">
                      {/* 프로필 카드 */}
                      <div className="flex flex-col items-center">
                        <div className="relative mb-2">
                          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 shadow-sm">
                            {userInfo.profile ? (
                              <img
                                src={userInfo.profile}
                                alt="프로필 이미지"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = DEFAULT_PROFILE_IMAGE;
                                }}
                              />
                            ) : (
                              <UserCircleIcon className="w-12 h-12 text-gray-400" />
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (profileFileInputRef.current) {
                                profileFileInputRef.current.click();
                              }
                            }}
                            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border border-gray-300 flex items-center justify-center shadow-sm hover:bg-gray-50"
                            disabled={isUploadingProfile}
                            title="프로필 사진 변경"
                          >
                            <PencilSquareIcon className="w-3.5 h-3.5 text-gray-500" />
                          </button>
                          <input
                            ref={profileFileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleProfileFileChange}
                            disabled={isUploadingProfile}
                          />
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-semibold text-gray-900">
                            {userInfo.name || '이름 없음'}
                          </div>
                          <div className="mt-0.5 text-[11px] text-gray-500">
                            {userInfo.email || userInfo.loginId || ''}
                          </div>
                          {userInfo.phone && (
                            <div className="mt-0.5 text-[11px] text-gray-500">
                              {userInfo.phone}
                            </div>
                          )}
                          <div className="mt-2 flex flex-col items-center space-y-1">
                            {profileUploadError && (
                              <div className="text-[11px] text-red-500 whitespace-pre-line">
                                {profileUploadError}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 입력 필드 영역 */}
                      {isEditingProfile && (
                        <div className="border-t border-gray-100 pt-4 space-y-3">
                          <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">
                              이름
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              value={userInfo.name || ''}
                              onChange={(e) => handleUserInfoFieldChange('name', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">
                              이메일
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              value={userInfo.email || ''}
                              onChange={(e) => handleUserInfoFieldChange('email', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">
                              휴대폰
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              value={userInfo.phone || ''}
                              onChange={(e) => handleUserInfoFieldChange('phone', e.target.value)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="py-8 text-center text-sm text-gray-500">사용자 정보를 불러올 수 없습니다.</div>
                  )}

                  {userInfoSaveError && (
                    <div className="mt-3 text-xs text-red-500 text-right">{userInfoSaveError}</div>
                  )}

                  <div className="mt-5 flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingProfile(false);
                        setIsUserInfoModalOpen(false);
                      }}
                      className="px-3 py-1.5 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                      disabled={isSavingUserInfo}
                    >
                      닫기
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveUserInfo}
                      className="px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                      disabled={isSavingUserInfo}
                    >
                      {isSavingUserInfo ? '저장 중...' : '저장'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* 데모 모드에서만 보이는 안내 배지 + 튜토리얼 말풍선 */}
      <DemoOverlay />

      {/* 말로 조회·입력하는 AI 어시스턴트 (오른쪽 아래 버튼) */}
      <AiChatPanel />
    </div>
  );
};

export default Layout;
